import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FiAlertTriangle, FiInfo, FiMessageCircle, FiX } from 'react-icons/fi';
import { interviewService } from '../services/interviewService';
import http, { getUserFacingError } from '../services/http';
import { readAuthUserId } from '../services/sessionStorage';
import { useInterview, InterviewQuestion } from '../context/InterviewContext';
import AvatarController from '../components/avatar/AvatarController';
import { formatSalaryRange } from '../utils/formatSalary';
import {
  resolveTranscriptionErrorOutcome,
  resolveTranscriptionPayloadOutcome,
  shouldConsumeNoAnswerAttempt,
} from '../utils/interviewTranscription';
import {
  createInterviewSttRecoveryCoordinator,
  InterviewSttRecoveryFailure,
  InterviewSttRecoveryPhase,
  isInterviewSttRecoveryCancelled,
} from '../utils/interviewSttRecovery';
import {
  createSubmitAttemptCoordinator,
  resolveSubmitFailureUiDecision,
  submitAnswerWithAttemptReconciliation,
  SubmitRecoveryFailure,
} from '../utils/interviewSubmitRecovery';
import {
  buildPendingAnswerRecoveryKey,
  clearPendingAnswerRecoveriesExcept,
  clearPendingAnswerRecovery,
  executePersistedAnswerSubmission,
  InterviewAnswerRecoveryPayload,
  pendingAnswerRestoreCoordinator,
  readLatestPendingAnswerRecoveryForJob,
  readPendingAnswerRecovery,
  resolvePendingAnswerRestoreDecision,
} from '../utils/interviewAnswerRecovery';
import {
  createFinalizationCompletionCoordinator,
  finishInterviewWithRecovery,
} from '../utils/interviewFinalizationRecovery';
import {
  createInterviewTraceId,
  interviewPerformanceNow,
  trackInterviewDuration,
  trackInterviewMetric,
} from '../utils/interviewObservability';
import {
  consumeInterviewVadSample,
  createInterviewVadAmbientState,
  createInterviewVadPolicyState,
  evaluateInterviewVadInitialNoSpeech,
  freezeInterviewVadAmbientProfile,
  INTERVIEW_VAD_POLICY,
  InterviewVadAmbientState,
  InterviewVadPolicyState,
  InterviewVadPolicyTerminationReason,
  isInterviewVadRecordingLikelySilent,
  observeInterviewVadAmbientSample,
} from '../utils/interviewVadPolicy';
import {
  acquireInterviewMediaStream,
  attachInterviewMediaStream,
  InterviewMediaAcquisitionOutcome,
  isInterviewMediaStreamUsable,
  stopInterviewMediaStream,
} from '../utils/interviewMediaHandoff';
import {
  buildTemiQuestionSegments,
  createPersonalizedIntroPlaybackGuard,
  resolveInterviewStartupOpening,
} from '../utils/interviewOpening';
import {
  captureInterviewSessionStartupNavigation,
  consumeInterviewSessionStartupHistoryState,
  resolveEffectiveInterviewSessionMode,
} from '../utils/interviewStartupPreparation';
import {
  interviewVideoUploadQueue,
  InterviewVideoUploadSnapshot,
  submitAnswerWithoutWaitingForVideo,
} from '../utils/interviewVideoUploadQueue';
import {
  buildAuthoritativeQuestionDeliveryId,
  createInterviewQuestionDeliveryCoordinator,
  InterviewQuestionDeliverySnapshot,
  isInterviewQuestionDeliveryCancelled,
} from '../utils/interviewQuestionDelivery';
import {
  clearInterviewTechnicalInterruptionIntent,
  InterviewTechnicalInterruptionCheckpoint,
  InterviewTechnicalInterruptionReasonCode,
  persistInterviewTechnicalInterruptionIntent,
} from '../utils/interviewTechnicalInterruption';

type Phase =
  | 'loading'
  | 'greeting'
  | 'ai_speaking'
  | 'responding'
  | 'idle'
  | 'recording'
  | 'prompting'
  | 'review'
  | 'submitting'
  | 'closing'
  | 'done'
  | 'finishing'
  | 'delivery_recovery'
  | 'technical_interrupted';

type MediaAvailabilityState = 'loading' | 'ready' | 'blocked' | 'lost';

const ttsSupported = typeof window !== 'undefined' && !!window.speechSynthesis;
const mediaRecorderSupported = typeof window !== 'undefined' && !!window.MediaRecorder;
const femaleVoiceHints = ['female', 'woman', 'gadis', 'siti', 'tuti', 'putri', 'indah'];

type TranscriptQuality = {
  score: number;
  needs_review: boolean;
  prefer_raw: boolean;
  review_reason?: string | null;
  flags: string[];
  token_overlap_ratio: number;
  length_ratio: number;
  preserved_glossary_terms: string[];
  missing_glossary_terms: string[];
  risky_shift_detected?: boolean;
  risky_shift_terms?: string[];
} | null;

type TranscriptQualityBand = 'good' | 'review' | 'critical' | 'unknown';

const resolveTranscriptQualityBand = (quality: TranscriptQuality): TranscriptQualityBand => {
  const qualityScore = Number.isFinite(Number(quality?.score)) ? Number(quality?.score) : null;
  const qualityFlags = Array.isArray(quality?.flags) ? quality.flags : [];
  const hasCriticalQualityIssue = quality?.review_reason === 'empty_cleaned'
    || qualityFlags.includes('lost_all_tokens')
    || ((qualityScore !== null && qualityScore < 55) && !quality?.risky_shift_detected);

  if (hasCriticalQualityIssue) {
    return 'critical';
  }

  if (quality?.needs_review || quality?.risky_shift_detected || (qualityScore !== null && qualityScore < 70)) {
    return 'review';
  }

  if (qualityScore !== null) {
    return 'good';
  }

  return 'unknown';
};

type TranscriptMode = 'raw_safe' | 'safe_assist' | 'assisted_clean' | '';
type AnswerMode = 'fallback_evaluation' | 'assisted_evaluation' | 'standard_evaluation' | '';
type SubmitActivity = 'idle' | 'evaluating' | 'reconciling';
type PendingVideoUpload = {
  blob: Blob;
  clientAttemptId: string;
  recordedAt: string;
  durationSeconds: number;
  questionId: number;
} | null;

type RecordedAnswerSaveInput = {
  question: InterviewQuestion;
  clientAttemptId: string;
  answerText: string;
  rawAnswerText: string;
  transcriptQuality: TranscriptQuality;
  transcriptMode: TranscriptMode;
  elapsedSeconds: number;
};

type ActiveTurnTiming = {
  traceId: string;
  clientAttemptId: string | null;
  questionId: number | null;
  startedAt: number;
  firstAudioLogged: boolean;
  listeningLogged: boolean;
  vadSpeechEndDecisionAt?: number | null;
};

type VadTerminationReason =
  | InterviewVadPolicyTerminationReason
  | 'media_loss'
  | 'technical_interruption'
  | 'deliberate_termination'
  | 'recorder_error';

type VadSummarySnapshot = {
  recordingDurationMs: number | null;
  peakRms: number | null;
  averageRms: number | null;
  speechDurationMs: number | null;
  strongSpeechDurationMs: number | null;
  speechRatio: number | null;
  speechStarted: boolean;
  vadSampleCount: number;
};

type ActiveVadMeasurement = {
  identity: string;
  traceId: string;
  questionId: number | null;
  deliveryCompletedAt: number | null;
  recorderStartedAt: number | null;
  firstSpeechAt: number | null;
  lastSpeechAt: number | null;
  vadDecisionAt: number | null;
  stopRequestedAt: number | null;
  blobReadyAt: number | null;
  sttStartedAt: number | null;
  terminationReason: VadTerminationReason | null;
  summarySnapshot: VadSummarySnapshot | null;
  summaryEmitted: boolean;
  emittedMetrics: Set<string>;
  recorder: MediaRecorder | null;
};

type PendingMediaSetup = {
  requestId: number;
  startedAt: number;
  outcome: InterviewMediaAcquisitionOutcome | 'handoff_playback_failed_reacquired';
  reusedHandoff: boolean;
  attaching: boolean;
  completed: boolean;
};

type AvatarEmotion = 'neutral' | 'happy' | 'serious' | 'thinking' | 'surprised';
type AvatarIntent =
  | 'friendly'
  | 'encouraging'
  | 'evaluating'
  | 'processing'
  | 'reviewing'
  | 'explaining'
  | 'celebrating'
  | 'warning'
  | 'low_quality';

type ReviewCardState = 'critical' | 'review' | 'ready';
type InterviewSessionMode = 'new' | 'resume';
type ConversationSpeaker = 'temi' | 'candidate' | 'system';
type ConversationTurnStatus = 'speaking' | 'listening' | 'processing' | 'saved' | 'error';
type ConversationTurn = {
  id: string;
  speaker: ConversationSpeaker;
  text: string;
  status?: ConversationTurnStatus;
  questionId?: number | null;
  createdAt: number;
};
type InterviewProgressPayload = {
  current_question_id?: number | null;
  current_question_position?: number | null;
  answered_question_ids?: number[];
  answered_count?: number;
  total_questions?: number;
  can_finish?: boolean;
  session_mode?: InterviewSessionMode | string | null;
} | null;
type InterviewJobDetail = {
  [key: string]: any;
  id?: number;
  title?: string;
  company?: string | null;
  type?: string | null;
  status?: string | null;
  salary_min?: number | string | null;
  salary_max?: number | string | null;
  salary?: string | null;
  salary_range?: string | null;
  posted_date?: string | null;
  location?: {
    kecamatan?: string | null;
    kota?: string | null;
    provinsi?: string | null;
    name?: string | null;
    address?: string | null;
  } | null;
  location_text?: string | null;
  placement?: string | null;
  work_location?: string | null;
  address?: string | null;
  experience_level?: { name?: string | null } | null;
  education_level?: { name?: string | null } | null;
  skills?: Array<{ id?: number; name?: string | null }>;
  positions?: Array<{ position_loker_id?: number; position_loker_name?: string | null }>;
  description?: string | null;
  responsibilities?: string | null;
  requirements?: string | null;
  benefits?: string | null;
};
type InterviewInterruptionIntent =
  | 'answer'
  | 'repeat_question'
  | 'clarify_question'
  | 'ask_example'
  | 'ask_job_info'
  | 'not_ready'
  | 'technical_issue'
  | 'restart_answer'
  | 'end_request';
type InterviewInterruptionResolution = {
  intent: InterviewInterruptionIntent;
  responseText?: string;
  repeatQuestion?: boolean;
  endSession?: boolean;
};

const VIDEO_RECORDING_BITS_PER_SECOND = 450_000;
const AUDIO_RECORDING_BITS_PER_SECOND = 128_000;
const AUTO_CONVERSATION_ENABLED = true;
const AUTO_LISTEN_DELAY_MS = INTERVIEW_VAD_POLICY.postTtsListeningDelayMs;
const MAX_NO_ANSWER_PROMPTS = 3;
const MAX_CANDIDATE_QUESTIONS = 3;
const VIDEO_RECORDER_STOP_TIMEOUT_MS = 1800;
const AUDIO_RECORDER_STOP_TIMEOUT_MS = 5000;

const measuredDurationBetween = (startedAt: number | null, completedAt: number | null) => {
  if (startedAt === null || completedAt === null) return null;
  if (!Number.isFinite(Number(startedAt)) || !Number.isFinite(Number(completedAt))) return null;
  return Math.round((Number(completedAt) - Number(startedAt)) * 1000) / 1000;
};

const roundVadMeasurement = (value: number | null, precision = 6) => {
  if (!Number.isFinite(Number(value))) return null;
  const factor = 10 ** precision;
  return Math.round(Number(value) * factor) / factor;
};

const debugInterviewSession = (...args: any[]) => {
  if (process.env.NODE_ENV !== 'development') return;
  // eslint-disable-next-line no-console
  console.info('[InterviewSession]', ...args);
};

const resolveReviewCardState = ({
  transcribeError,
  isLowQualityScore,
  requiresTranscriptReview,
  shouldShowTranscriptNotice,
  transcriptMode,
}: {
  transcribeError: boolean;
  isLowQualityScore: boolean;
  requiresTranscriptReview: boolean;
  shouldShowTranscriptNotice: boolean;
  transcriptMode: TranscriptMode;
}): ReviewCardState => {
  if (transcribeError || isLowQualityScore) {
    return 'critical';
  }

  if (requiresTranscriptReview || shouldShowTranscriptNotice || transcriptMode === 'raw_safe' || transcriptMode === 'safe_assist') {
    return 'review';
  }

  return 'ready';
};

const MEDIA_CAPTURE_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    width: { ideal: 640, max: 640 },
    height: { ideal: 480, max: 480 },
    frameRate: { ideal: 15, max: 15 },
    facingMode: 'user',
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  },
};

const buildMediaCaptureConstraints = (cameraDeviceId?: string, microphoneDeviceId?: string): MediaStreamConstraints => {
  const video: MediaTrackConstraints = {
    ...(MEDIA_CAPTURE_CONSTRAINTS.video as MediaTrackConstraints),
  };
  const audio: MediaTrackConstraints = {
    ...(MEDIA_CAPTURE_CONSTRAINTS.audio as MediaTrackConstraints),
  };

  if (cameraDeviceId) {
    video.deviceId = { exact: cameraDeviceId };
  }
  if (microphoneDeviceId) {
    audio.deviceId = { exact: microphoneDeviceId };
  }

  return { video, audio };
};

const buildClientAttemptId = (questionId?: number | null) => {
  const qid = Number.isFinite(Number(questionId)) ? Number(questionId) : 0;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `q${qid}-${crypto.randomUUID()}`;
  }
  return `q${qid}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const SUBMIT_RECOVERY_OPTIONS = {
  maxTransientAttempts: 4,
  transientDelayMs: 500,
  maxProcessingPolls: 12,
  processingDelayMs: 2000,
  maxProcessingDelayMs: 2000,
  maxRateLimitAttempts: 1,
  maxAutomaticRateLimitDelayMs: 5000,
};

const isMeaningfulAnswerTranscript = (value: string) => {
  const normalized = String(value || '').replace(/\s+/g, ' ').trim();
  if (normalized.length < 12) return false;
  const tokens = normalized.split(' ').filter((token) => token.length > 1);
  if (tokens.length < 3) return false;
  return !/^(ya|iya|oke|ok|hmm|emm|eee|halo|tes|test)[\s.!,?]*$/i.test(normalized);
};

const normalizeTranscriptForMatch = (value: string) => String(value || '')
  .toLowerCase()
  .replace(/[^\p{L}\p{N}\s]/gu, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getTranscriptTokens = (value: string) => normalizeTranscriptForMatch(value)
  .split(' ')
  .filter((token) => token.length > 2);

const hasPromptLikePhrase = (value: string) => /\b(ceritakan|menurut kamu|bagaimana|apa yang|kenapa|sekarang saya|saya ingin|dari pengalaman|situasi berikutnya)\b/i.test(value);

const isLikelyTemiPromptEcho = (answerText: string, temiTexts: string | string[]) => {
  const normalizedAnswer = normalizeTranscriptForMatch(answerText);
  if (!normalizedAnswer) return false;

  const answerTokens = getTranscriptTokens(normalizedAnswer);
  if (answerTokens.length < 4) return false;

  const candidates = (Array.isArray(temiTexts) ? temiTexts : [temiTexts])
    .map((text) => normalizeTranscriptForMatch(text))
    .filter(Boolean);

  return candidates.some((normalizedQuestion) => {
    if (normalizedQuestion.includes(normalizedAnswer)) return true;

    const questionTokens = new Set(getTranscriptTokens(normalizedQuestion));
    if (!questionTokens.size) return false;

    const overlappingTokens = answerTokens.filter((token) => questionTokens.has(token)).length;
    const overlapRatio = overlappingTokens / answerTokens.length;
    return overlapRatio >= 0.62 || (overlapRatio >= 0.48 && hasPromptLikePhrase(normalizedAnswer));
  });
};

const resolveCandidateDisplayName = () => {
  if (typeof window === 'undefined') return 'Kamu';

  try {
    const rawUser = window.localStorage.getItem('authUser') || window.localStorage.getItem('user');
    if (!rawUser) return 'Kamu';
    const user = JSON.parse(rawUser);
    const candidates = [
      user?.name,
      user?.full_name,
      user?.fullname,
      user?.display_name,
      user?.username,
      user?.email,
    ];
    const resolved = candidates
      .map((item) => String(item || '').trim())
      .find(Boolean);
    return resolved || 'Kamu';
  } catch (_) {
    return 'Kamu';
  }
};

const resolveSessionJobTitle = (routeState: any, jobDetail?: InterviewJobDetail | null) => {
  const candidates = [
    jobDetail?.title,
    jobDetail?.job_title,
    jobDetail?.jobTitle,
    jobDetail?.name,
    ...(Array.isArray(jobDetail?.positions)
      ? jobDetail.positions.map((position: any) => position?.position_loker_name || position?.name || position?.position)
      : []),
    routeState?.jobTitle,
    routeState?.job?.title,
    routeState?.application?.job?.title,
    routeState?.interview?.job?.title,
    routeState?.position,
    routeState?.job_name,
    routeState?.jobName,
  ];
  return candidates
    .map((item) => String(item || '').trim())
    .find(Boolean) || 'Lowongan yang dilamar';
};

const buildTemiClosingUtterance = () => (
  'Baik, terima kasih. Kalau begitu sesi interview kita cukup sampai di sini. Sekarang saya siapkan hasil interview kamu.'
);

const cleanJobKnowledgeText = (value: unknown, maxLength = 360) => {
  const cleaned = String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned || cleaned.length <= maxLength) return cleaned;
  return `${cleaned.slice(0, maxLength).replace(/\s+\S*$/, '')}...`;
};

const joinReadableList = (items: string[]) => {
  const safeItems = items.map((item) => cleanJobKnowledgeText(item, 80)).filter(Boolean);
  if (!safeItems.length) return '';
  if (safeItems.length === 1) return safeItems[0];
  if (safeItems.length === 2) return `${safeItems[0]} dan ${safeItems[1]}`;
  return `${safeItems.slice(0, -1).join(', ')}, dan ${safeItems[safeItems.length - 1]}`;
};

const buildJobTitleLabel = (jobDetail: InterviewJobDetail | null) => (
  cleanJobKnowledgeText(jobDetail?.title, 100) || 'posisi ini'
);

const pickJobDetailText = (jobDetail: InterviewJobDetail | null, keys: string[], maxLength = 220) => {
  if (!jobDetail) return '';
  for (const key of keys) {
    const value = cleanJobKnowledgeText(jobDetail?.[key], maxLength);
    if (value && value !== '0') return value;
  }
  return '';
};

const buildJobLocationLabel = (jobDetail: InterviewJobDetail | null) => {
  const location = jobDetail?.location || null;
  return joinReadableList([
    String(location?.name || ''),
    String(location?.kecamatan || ''),
    String(location?.kota || ''),
    String(location?.provinsi || ''),
    String(location?.address || ''),
    pickJobDetailText(jobDetail, ['location_text', 'placement', 'work_location', 'address'], 120),
  ]);
};

const buildDiplomaticJobFallback = (jobDetail: InterviewJobDetail | null) => {
  const titleLabel = buildJobTitleLabel(jobDetail);
  const description = cleanJobKnowledgeText(jobDetail?.description, 220);
  if (description) {
    return `Untuk detail itu, informasi lowongan belum menjelaskan secara spesifik. Gambaran yang tercantum untuk ${titleLabel}: ${description}. Detail lanjutannya biasanya dikonfirmasi oleh tim rekrutmen pada tahap berikutnya.`;
  }
  return `Untuk detail itu, informasi lowongan belum menjelaskan secara spesifik. Tim rekrutmen biasanya akan mengonfirmasi detail lanjutannya pada tahap berikutnya.`;
};

const normalizeCandidateQuestionText = (value: string) => cleanJobKnowledgeText(value, 500).toLowerCase();
const includesCandidateQuestionTopic = (normalizedQuestion: string, pattern: RegExp) => pattern.test(normalizedQuestion);
const isSalaryQuestionText = (value: string) => {
  const normalized = normalizeCandidateQuestionText(value);
  return /\bgaji(?:\s*nya)?\b|\bgajih(?:\s*nya)?\b|\b(salary|upah|kompensasi|take home pay|thp|nominal)\b|\b(range|kisaran)\s+gaji\b/i.test(normalized);
};
const isLocationQuestionText = (value: string) => /\b(lokasi|penempatan|alamat|cabang|kota|remote|wfh|wfo|hybrid|kantor|tempat kerja|tempat ini|dimana|di mana)\b/i.test(value);

const parseSalaryAmount = (value: unknown) => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null;
    return value < 1000 ? value * 1_000_000 : value;
  }

  const text = String(value || '').trim().toLowerCase();
  if (!text || text === '0') return null;

  const jutaMatch = text.match(/(\d+(?:[.,]\d+)?)\s*(?:jt|juta|million|mio)\b/i);
  if (jutaMatch) {
    const jutaValue = Number(jutaMatch[1].replace(',', '.'));
    return Number.isFinite(jutaValue) && jutaValue > 0 ? jutaValue * 1_000_000 : null;
  }

  const withoutCurrency = text
    .replace(/\b(rp|idr)\b/gi, '')
    .replace(/\s+/g, '')
    .replace(/[.,]\d{2}$/g, '');
  const digitsOnly = withoutCurrency.replace(/[^\d]/g, '');
  if (!digitsOnly) return null;

  const amount = Number(digitsOnly);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount < 1000 ? amount * 1_000_000 : amount;
};

const extractSalaryLabelFromText = (value: string) => {
  const text = cleanJobKnowledgeText(value, 700);
  if (!text) return '';

  const rangeMatch = text.match(/(?:rp\s*)?(\d+(?:[.,]\d+)?)\s*(?:-|sampai|hingga|s\/d|sd)\s*(?:rp\s*)?(\d+(?:[.,]\d+)?)\s*(jt|juta|million|mio)?/i);
  if (rangeMatch) {
    const unit = rangeMatch[3] || '';
    const min = parseSalaryAmount(`${rangeMatch[1]} ${unit}`);
    const max = parseSalaryAmount(`${rangeMatch[2]} ${unit}`);
    if (min && max) return formatSalaryRange(min, max);
  }

  const singleMatch = text.match(/(?:gaji|salary|upah|kompensasi)[^\d]*(?:rp\s*)?(\d+(?:[.,]\d+)?)\s*(jt|juta|million|mio)?/i);
  if (singleMatch) {
    const amount = parseSalaryAmount(`${singleMatch[1]} ${singleMatch[2] || ''}`);
    if (amount) return formatSalaryRange(amount, amount);
  }

  return '';
};

const resolveJobSalaryLabel = (jobDetail: InterviewJobDetail | null) => {
  const rawSalary = pickJobDetailText(jobDetail, [
    'salary',
    'salary_range',
    'salaryRange',
    'range_salary',
    'gaji',
    'salary_label',
  ]);
  if (rawSalary) return rawSalary;

  const minSalary = parseSalaryAmount(
    jobDetail?.salary_min ??
    jobDetail?.min_salary ??
    jobDetail?.salaryMinimum ??
    jobDetail?.salary_from
  );
  const maxSalary = parseSalaryAmount(
    jobDetail?.salary_max ??
    jobDetail?.max_salary ??
    jobDetail?.salaryMaximum ??
    jobDetail?.salary_to
  );
  const hasMinSalary = Number.isFinite(Number(minSalary)) && Number(minSalary) > 0;
  const hasMaxSalary = Number.isFinite(Number(maxSalary)) && Number(maxSalary) > 0;

  if (hasMinSalary && hasMaxSalary) {
    return formatSalaryRange(Number(minSalary), Number(maxSalary));
  }
  if (hasMinSalary) {
    return `mulai dari ${formatSalaryRange(Number(minSalary), Number(minSalary))}`;
  }
  if (hasMaxSalary) {
    return `hingga ${formatSalaryRange(Number(maxSalary), Number(maxSalary))}`;
  }

  return extractSalaryLabelFromText([
    jobDetail?.description,
    jobDetail?.responsibilities,
    jobDetail?.requirements,
    jobDetail?.benefits,
  ].filter(Boolean).join(' '));
};

const buildJobKnowledgeResponse = (answerText: string, jobDetail: InterviewJobDetail | null) => {
  const normalizedQuestion = normalizeCandidateQuestionText(answerText);
  const titleLabel = buildJobTitleLabel(jobDetail);
  const salaryLabel = resolveJobSalaryLabel(jobDetail);
  const benefits = cleanJobKnowledgeText(jobDetail?.benefits);
  const responsibilities = cleanJobKnowledgeText(jobDetail?.responsibilities);
  const description = cleanJobKnowledgeText(jobDetail?.description);
  const requirements = cleanJobKnowledgeText(jobDetail?.requirements);
  const education = cleanJobKnowledgeText(jobDetail?.education_level?.name, 80);
  const experience = cleanJobKnowledgeText(jobDetail?.experience_level?.name, 80);
  const location = buildJobLocationLabel(jobDetail);
  const skills = joinReadableList((jobDetail?.skills || []).map((skill) => String(skill?.name || '')));
  const positions = joinReadableList((jobDetail?.positions || []).map((position) => String(position?.position_loker_name || '')));
  const company = cleanJobKnowledgeText(jobDetail?.company, 120);
  const jobType = cleanJobKnowledgeText(jobDetail?.type, 80);
  const asksSalary = isSalaryQuestionText(answerText);
  const asksLocation = isLocationQuestionText(answerText);

  if (asksSalary && asksLocation) {
    const parts = [
      salaryLabel ? `range gaji yang tercantum adalah ${salaryLabel}` : 'range gaji belum dicantumkan secara spesifik di detail lowongan',
      location ? `lokasi atau penempatannya adalah ${location}` : '',
    ].filter(Boolean);
    if (parts.length) {
      return `Untuk ${titleLabel}, ${parts.join(', dan ')}. Detail finalnya tetap mengikuti proses rekrutmen dan kebijakan perusahaan.`;
    }
    return buildDiplomaticJobFallback(jobDetail);
  }

  if (asksSalary) {
    if (!salaryLabel) return buildDiplomaticJobFallback(jobDetail);
    return `Untuk ${titleLabel}, range gaji yang tercantum di detail lowongan adalah ${salaryLabel}. Detail finalnya tetap mengikuti proses rekrutmen dan kebijakan perusahaan.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(benefit|benefitnya|keuntungan|tunjangan|fasilitas|asuransi|bpjs|komisi|bonus|insentif)\b/i)) {
    if (!benefits) return buildDiplomaticJobFallback(jobDetail);
    return `Benefit atau keuntungan yang tercantum untuk ${titleLabel} adalah: ${benefits}.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(tugas|tanggung jawab|job\s?desc|pekerjaan|kerjaan|sehari|scope|lingkup|aktivitas)\b/i)) {
    const responsibilityText = responsibilities || description;
    if (!responsibilityText) return buildDiplomaticJobFallback(jobDetail);
    return `Gambaran tugas untuk ${titleLabel}: ${responsibilityText}.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(syarat|persyaratan|kualifikasi|requirement|pengalaman|pendidikan|skill|keahlian|kemampuan)\b/i)) {
    const qualificationParts = [
      requirements ? `persyaratan: ${requirements}` : '',
      education ? `pendidikan: ${education}` : '',
      experience ? `level pengalaman: ${experience}` : '',
      skills ? `keahlian yang dicantumkan: ${skills}` : '',
    ].filter(Boolean);
    if (!qualificationParts.length) return buildDiplomaticJobFallback(jobDetail);
    return `Untuk ${titleLabel}, ${qualificationParts.join('; ')}.`;
  }

  if (asksLocation || includesCandidateQuestionTopic(normalizedQuestion, /\b(lokasi|penempatan|alamat|cabang|kota|remote|wfh|wfo|hybrid|kantor|tempat kerja|tempat ini)\b/i)) {
    if (!location) return buildDiplomaticJobFallback(jobDetail);
    return `Lokasi atau penempatan yang tercantum untuk ${titleLabel} adalah ${location}.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(perusahaan|company|kantor|klien)\b/i)) {
    if (!company) return buildDiplomaticJobFallback(jobDetail);
    return `Lowongan ${titleLabel} tercantum untuk perusahaan ${company}.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(kontrak|full\s?time|part\s?time|status|tipe|jenis pekerjaan|durasi)\b/i)) {
    if (!jobType) return buildDiplomaticJobFallback(jobDetail);
    return `Tipe pekerjaan yang tercantum untuk ${titleLabel} adalah ${jobType}.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(posisi|role|jabatan|lowongan|divisi)\b/i)) {
    const positionText = positions || titleLabel;
    return `Posisi yang tercantum adalah ${positionText}.`;
  }

  if (includesCandidateQuestionTopic(normalizedQuestion, /\b(proses|tahap|selanjutnya|next|pengumuman|hasil|kabar|timeline)\b/i)) {
    return 'Setelah sesi ini, hasil interview akan diproses oleh sistem dan tim rekrutmen. Tahap berikutnya biasanya akan diinformasikan melalui status lamaran atau komunikasi resmi dari tim TEMA.';
  }

  return buildDiplomaticJobFallback(jobDetail);
};

const buildCandidateQuestionResponse = ({
  question,
  answerText,
  jobDetail,
}: {
  question: InterviewQuestion;
  answerText: string;
  jobDetail: InterviewJobDetail | null;
}) => {
  if (question?.question_type !== 'candidate_question') return '';
  return buildJobKnowledgeResponse(answerText, jobDetail);
};

const isCandidateDoneAsking = (value: string) => {
  const normalized = normalizeCandidateQuestionText(value)
    .replace(/[.,!?]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return true;
  if (normalized.length > 90) return false;

  return /^(tidak|nggak|ngga|enggak|gak|ga|belum|cukup|sudah cukup|itu saja|itu aja|no|nothing|none)(\s|$)/i.test(normalized) ||
    /\b(tidak ada|nggak ada|ngga ada|gak ada|ga ada|belum ada|sudah cukup|cukup itu saja|cukup itu aja|tidak ada lagi|gak ada lagi|ga ada lagi)\b/i.test(normalized);
};

const buildCandidateQuestionReportAnswer = (questions: string[], candidateDoneAsking: boolean) => {
  const normalizedQuestions = questions
    .map((item) => cleanJobKnowledgeText(item, 260))
    .filter(Boolean);

  if (!normalizedQuestions.length) {
    return candidateDoneAsking
      ? 'Kandidat menyampaikan tidak ada pertanyaan.'
      : 'Kandidat belum menyampaikan pertanyaan.';
  }

  const questionLines = normalizedQuestions.map((item, index) => `${index + 1}. ${item}`);
  return candidateDoneAsking
    ? `${questionLines.join('\n')}\nKandidat menyampaikan tidak ada pertanyaan tambahan.`
    : questionLines.join('\n');
};

const buildCandidateQuestionFollowupPrompt = (remainingQuestions: number) => {
  if (remainingQuestions <= 1) {
    return 'Saya sudah jawab. Kamu masih punya satu kesempatan lagi jika ada hal lain yang ingin ditanyakan.';
  }

  return `Saya sudah jawab. Masih ada hal lain yang ingin kamu tanyakan? Kamu masih punya ${remainingQuestions} kesempatan bertanya.`;
};

const hasQuestionTone = (value: string) => /\?|(\b(apa|apakah|berapa|bagaimana|gimana|dimana|di mana|kapan|boleh|bisa|dapat|mau tanya|ingin tanya|nanya|tanya)\b)/i.test(value);
const isLikelyJobInfoQuestion = (value: string) => {
  const normalized = normalizeCandidateQuestionText(value);
  if (!hasQuestionTone(normalized)) return false;
  if (isSalaryQuestionText(normalized)) return true;
  return /\b(benefit|keuntungan|tunjangan|fasilitas|asuransi|bpjs|komisi|bonus|insentif|tugas|tanggung jawab|job\s?desc|pekerjaan|scope|syarat|persyaratan|kualifikasi|requirement|pengalaman|pendidikan|skill|keahlian|lokasi|penempatan|tempat kerja|tempat ini|alamat|cabang|remote|wfh|wfo|hybrid|perusahaan|company|kontrak|full\s?time|part\s?time|status|tipe|posisi|role|jabatan|lowongan|proses|tahap|selanjutnya|pengumuman|hasil|timeline)\b/i.test(normalized);
};

const buildInterviewInterruptionResolution = ({
  answerText,
  question,
  jobDetail,
}: {
  answerText: string;
  question: InterviewQuestion;
  jobDetail: InterviewJobDetail | null;
}): InterviewInterruptionResolution => {
  if (question?.question_type === 'candidate_question') return { intent: 'answer' };

  const normalized = normalizeCandidateQuestionText(answerText)
    .replace(/[.,!?]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const shortUtterance = normalized.length <= 140;
  const currentQuestionText = cleanJobKnowledgeText(question?.question_text, 260);

  if (shortUtterance && /\b(ulang|ulangi|diulang|repeat|pertanyaan tadi|pertanyaannya apa|bisa ulang|boleh ulang)\b/i.test(normalized)) {
    return {
      intent: 'repeat_question',
      responseText: 'Tentu, saya ulangi pertanyaannya.',
      repeatQuestion: true,
    };
  }

  const asksForClarification = shortUtterance && (
    /\b(kurang paham|tidak paham|nggak paham|ga paham|belum paham|bingung|bisa dijelaskan|jelaskan lagi|pertanyaannya kurang jelas)\b/i.test(normalized) ||
    (
      hasQuestionTone(normalized) &&
      /\b(maksud|dimaksud|artinya|arti|konteks|konteksnya|bagian mana|yang mana|seperti apa)\b/i.test(normalized)
    )
  );
  if (asksForClarification) {
    const clarifyResponseText = /\bkonteks/i.test(normalized)
      ? 'Yang dimaksud konteks adalah latar situasinya: masalah apa yang terjadi, kapan atau di mana terjadi, siapa yang terlibat, dan kendalanya apa. Setelah itu, ceritakan langkah yang kamu ambil dan hasilnya. Silakan lanjut jawab dari pengalamanmu sendiri.'
      : 'Boleh. Intinya saya ingin memahami contoh nyata dari pengalamanmu: situasinya apa, peranmu apa, langkah yang kamu ambil, dan hasilnya. Silakan jawab pertanyaan tadi dengan alur itu.';
    return {
      intent: 'clarify_question',
      responseText: clarifyResponseText,
      repeatQuestion: false,
    };
  }

  if (shortUtterance && /\b(contoh|contohnya|misalnya|jawabnya gimana|cara jawab|format jawab)\b/i.test(normalized)) {
    return {
      intent: 'ask_example',
      responseText: 'Bisa. Gunakan format singkat seperti ini: situasinya apa, peran kamu apa, langkah yang kamu ambil, lalu hasilnya seperti apa. Ambil contoh dari pengalamanmu sendiri ya. Silakan jawab dengan format itu.',
      repeatQuestion: false,
    };
  }

  if (isLikelyJobInfoQuestion(answerText)) {
    return {
      intent: 'ask_job_info',
      responseText: `${buildJobKnowledgeResponse(answerText, jobDetail)} Setelah itu, kita kembali ke pertanyaan interviewnya.`,
      repeatQuestion: Boolean(currentQuestionText),
    };
  }

  if (shortUtterance && /\b(sebentar|tunggu|nanti dulu|belum siap|pause|jeda|minum|pikir dulu|satu menit)\b/i.test(normalized)) {
    return {
      intent: 'not_ready',
      responseText: 'Tidak apa-apa, saya tunggu sebentar. Saat sudah siap, kamu bisa jawab pertanyaan ini.',
      repeatQuestion: true,
    };
  }

  if (shortUtterance && /\b(suara|audio|mic|mikrofon|kamera|kedengeran|terdengar|putus|lag|error|macet|mati|koneksi)\b/i.test(normalized)) {
    return {
      intent: 'technical_issue',
      responseText: 'Baik, coba pastikan mikrofon dan koneksi kamu stabil. Saya ulangi pertanyaannya, lalu kamu bisa jawab lagi.',
      repeatQuestion: true,
    };
  }

  if (shortUtterance && /\b(saya ulang|jawab ulang|ulang dari awal|mulai lagi|maksud saya|koreksi|ralat)\b/i.test(normalized)) {
    return {
      intent: 'restart_answer',
      responseText: 'Baik, silakan jawab ulang dari awal. Saya akan mendengarkan jawabanmu lagi.',
      repeatQuestion: false,
    };
  }

  if (shortUtterance && /\b(akhiri|selesai saja|sudahi|berhenti|stop interview|tidak lanjut|nggak lanjut|ga lanjut)\b/i.test(normalized)) {
    return {
      intent: 'end_request',
      responseText: 'Baik, saya akhiri sesi interviewnya di sini. Terima kasih sudah mengikuti proses interview ini.',
      endSession: true,
    };
  }

  return { intent: 'answer' };
};

const buildNoAnswerPromptText = (attempt: number) => {
  if (attempt >= MAX_NO_ANSWER_PROMPTS) {
    return 'Saya masih belum menerima jawaban yang jelas. Supaya sesi tidak menggantung, saya akan mengakhiri interview ini sekarang.';
  }

  const prompts = [
    'Saya belum menangkap jawabanmu. Apakah kamu masih di sana? Kalau sudah siap, silakan jawab pertanyaan yang sama.',
    'Sepertinya suara kamu belum masuk. Saya tunggu sebentar, silakan mulai bicara saat sudah siap.',
    'Jawabanmu belum terdengar jelas. Tidak apa-apa, coba jawab lagi dari awal dengan suara sedikit lebih dekat ke mikrofon.',
  ];
  return prompts[(Math.max(1, attempt) - 1) % prompts.length];
};

const TypingDots = () => (
  <span className="iv-typing-dots" aria-label="Memproses">
    <i />
    <i />
    <i />
  </span>
);

const buildConversationTurnsFromHistory = (
  questions: InterviewQuestion[],
  answers: any[],
): ConversationTurn[] => {
  const now = Date.now();
  const questionById = new Map(
    questions
      .map((question) => [Number(question.id), question] as const)
      .filter(([questionId]) => Number.isFinite(questionId) && questionId > 0)
  );
  const sortedAnswers = [...answers]
    .filter((answer) => Number(answer?.question_id) > 0 && String(answer?.answer_text || '').trim())
    .sort((left, right) => {
      const leftQuestion = questionById.get(Number(left.question_id));
      const rightQuestion = questionById.get(Number(right.question_id));
      const leftIndex = Number(leftQuestion?.question_index ?? Number.MAX_SAFE_INTEGER);
      const rightIndex = Number(rightQuestion?.question_index ?? Number.MAX_SAFE_INTEGER);
      if (leftIndex !== rightIndex) return leftIndex - rightIndex;
      return Number(left.question_id) - Number(right.question_id);
    });

  const turns: ConversationTurn[] = [];
  sortedAnswers.forEach((answer, index) => {
    const questionId = Number(answer.question_id);
    const question = questionById.get(questionId);
    const createdAt = now - ((sortedAnswers.length - index) * 2);

    if (question?.question_text) {
      turns.push({
        id: `history-temi-${questionId}-${index}`,
        speaker: 'temi',
        text: question.question_text,
        questionId,
        createdAt,
      });
    }
    turns.push({
      id: `history-candidate-${questionId}-${answer.id || index}`,
      speaker: 'candidate',
      text: String(answer.answer_text || '').trim(),
      status: 'saved',
      questionId,
      createdAt: createdAt + 1,
    });
  });

  return turns.slice(-48);
};

const resolveHistoricalAnswers = (detailPayload: any, cachedAnswers: any[]) => {
  const detailItems = Array.isArray(detailPayload?.questions_with_answers)
    ? detailPayload.questions_with_answers
    : [];
  const detailAnswers = detailItems
    .map((item: any) => item?.answer ? {
      ...item.answer,
      question_id: item.answer.question_id || item.id,
    } : null)
    .filter(Boolean);

  return detailAnswers.length ? detailAnswers : (Array.isArray(cachedAnswers) ? cachedAnswers : []);
};

const resolveMediaAccessErrorMessage = (error: any) => {
  if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
    return 'Izin kamera/mikrofon ditolak. Ubah ke Allow di browser lalu coba lagi.';
  }

  if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
    return 'Kamera atau mikrofon tidak ditemukan. Pastikan perangkat terhubung lalu coba lagi.';
  }

  return 'Kamera atau mikrofon belum siap digunakan. Periksa perangkat Anda lalu coba lagi.';
};

const pickTemiVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null => {
  if (!Array.isArray(voices) || voices.length === 0) return null;

  const scored = voices.map((voice) => {
    const label = `${voice.name} ${voice.voiceURI} ${voice.lang}`.toLowerCase();
    const isIndonesian = String(voice.lang || '').toLowerCase().startsWith('id');
    const isFemaleLike = femaleVoiceHints.some((hint) => label.includes(hint));

    let score = 0;
    if (isIndonesian) score += 10;
    if (isFemaleLike) score += 5;
    if (voice.default) score += 1;

    return { voice, score };
  });

  scored.sort((left, right) => right.score - left.score);
  return scored[0]?.score > 0 ? scored[0].voice : null;
};

const InterviewSession: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();
  const initialStartupNavigationRef = useRef(
    captureInterviewSessionStartupNavigation(state)
  );
  const initialStartupNavigation = initialStartupNavigationRef.current;
  const {
    interviewData,
    bindInterviewScope,
    setInterviewId,
    setJobId,
    setOutsourceId,
    setQuestions,
    addAnswer,
    setStatus,
    resetInterview,
  } = useInterview();

  const jobIdNum = jobId ? Number(jobId) : 0;
  const authenticatedUserId = useMemo(() => readAuthUserId(), []);
  const bootRecoveryLookup = useMemo(
    () => authenticatedUserId && jobIdNum
      ? readLatestPendingAnswerRecoveryForJob(authenticatedUserId, jobIdNum)
      : { status: 'missing' as const, envelope: null },
    [authenticatedUserId, jobIdNum]
  );
  const bootRecovery = bootRecoveryLookup.envelope;
  const bootRecoveryScope = bootRecoveryLookup.scope;
  const interviewId: number = state?.interviewId
    ?? interviewData.interviewId
    ?? bootRecovery?.interview_id
    ?? bootRecoveryScope?.interviewId
    ?? 0;
  const outsourceIdVal: number = state?.outsourceId
    ?? interviewData.outsourceId
    ?? bootRecovery?.outsource_id
    ?? bootRecoveryScope?.outsourceId
    ?? 0;
  const startupTraceIdRef = useRef<string>(
    initialStartupNavigation.observabilityTraceId || createInterviewTraceId('session')
  );
  const startupStartedAtRef = useRef<number>(
    initialStartupNavigation.startupStartedAt ?? interviewPerformanceNow()
  );
  const reusePreparedMediaRef = useRef(initialStartupNavigation.reusePreparedMedia);
  const sessionInitStartedAtRef = useRef(interviewPerformanceNow());
  const sessionInitLoggedRef = useRef(false);
  const startupFirstAudioLoggedRef = useRef(false);
  const activeTurnTimingRef = useRef<ActiveTurnTiming | null>(null);
  const pendingVadSpeechEndDecisionRef = useRef<{
    traceId: string;
    questionId: number | null;
    startedAt: number;
  } | null>(null);
  const selectedCameraDeviceId = String(state?.cameraDeviceId || '').trim();
  const selectedMicrophoneDeviceId = String(state?.microphoneDeviceId || '').trim();
  const mediaCaptureConstraints = useMemo(
    () => buildMediaCaptureConstraints(selectedCameraDeviceId, selectedMicrophoneDeviceId),
    [selectedCameraDeviceId, selectedMicrophoneDeviceId],
  );
  const candidateDisplayName = useMemo(() => resolveCandidateDisplayName(), []);
  const hasScopedInterviewCache = interviewData.jobId === jobIdNum
    && interviewData.outsourceId === outsourceIdVal;

  const [questions, setLocalQuestions] = useState<InterviewQuestion[]>(
    hasScopedInterviewCache && interviewData.questions.length > 0 ? interviewData.questions : []
  );
  const [currentIdx, setCurrentIdx] = useState(0);
  const [phase, setPhase] = useState<Phase>('loading');
  const [answer, setAnswer] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [sttRecoveryPhase, setSttRecoveryPhase] = useState<InterviewSttRecoveryPhase>('idle');
  const [, setSttTerminalFailure] = useState(false);
  const [questionDelivery, setQuestionDelivery] = useState<InterviewQuestionDeliverySnapshot>({
    state: 'authoritative',
    deliveryId: null,
    attempt: 0,
    waitMs: null,
  });
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [transcribeError, setTranscribeError] = useState(false);
  const [transcriptQuality, setTranscriptQuality] = useState<TranscriptQuality>(null);
  const [transcriptMode, setTranscriptMode] = useState<TranscriptMode>('');
  const [transcriptNotice, setTranscriptNotice] = useState('');
  const [videoUploadStates, setVideoUploadStates] = useState<InterviewVideoUploadSnapshot[]>(() => (
    interviewId ? interviewVideoUploadQueue.getInterviewSnapshot(interviewId) : []
  ));
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pendingSubmitAttemptId, setPendingSubmitAttemptId] = useState<string | null>(null);
  const [, setIsRetryingSubmit] = useState(false);
  const [submitActivity, setSubmitActivity] = useState<SubmitActivity>('idle');
  const [, setAnswerMode] = useState<AnswerMode>('');
  const [answerNotice, setAnswerNotice] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [finishRetryGeneration, setFinishRetryGeneration] = useState(0);
  const [backgroundSaveCount, setBackgroundSaveCount] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [mediaAvailability, setMediaAvailability] = useState<MediaAvailabilityState>('loading');
  const [mediaStreamRevision, setMediaStreamRevision] = useState(0);
  const [resumeNotice, setResumeNotice] = useState('');
  const [jobDetail, setJobDetail] = useState<InterviewJobDetail | null>(() => state?.job || null);
  const [conversationTurns, setConversationTurns] = useState<ConversationTurn[]>([]);
  const [transcriptPanelOpen, setTranscriptPanelOpen] = useState(true);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [technicalInterruptionMessage, setTechnicalInterruptionMessage] = useState('');
  const sessionJobTitle = useMemo(() => resolveSessionJobTitle(state, jobDetail), [jobDetail, state]);

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    consumeInterviewSessionStartupHistoryState({
      history: window.history,
      currentUrl: window.location.href,
    });
  }, []);

  useEffect(() => {
    if (!jobIdNum || !outsourceIdVal) return;
    bindInterviewScope({ jobId: jobIdNum, outsourceId: outsourceIdVal });
  }, [bindInterviewScope, jobIdNum, outsourceIdVal]);

  useEffect(() => {
    if (!interviewId) return undefined;

    interviewVideoUploadQueue.retainInterview(interviewId);
    const unsubscribe = interviewVideoUploadQueue.subscribe((snapshot) => {
      setVideoUploadStates(snapshot.filter((task) => task.interviewId === interviewId));
    });

    return () => {
      unsubscribe();
      interviewVideoUploadQueue.releaseInterview(interviewId);
    };
  }, [interviewId]);

  useEffect(() => {
    jobDetailRef.current = jobDetail;
  }, [jobDetail]);

  useEffect(() => {
    if (!jobIdNum) return undefined;

    let isMounted = true;
    http.get(`jobs/${jobIdNum}`)
      .then((response) => {
        if (!isMounted) return;
        const nextJobDetail = response.data?.data || null;
        setJobDetail(nextJobDetail);
      })
      .catch(() => {
        // Salary answer falls back gracefully if job detail cannot be loaded.
      });

    return () => {
      isMounted = false;
    };
  }, [jobIdNum]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<any>(null);
  const videoRecRef = useRef<any>(null);
  const recorderStartLockRef = useRef(false);
  const audioStopTimeoutRef = useRef<number | null>(null);
  const audioStopFallbackRef = useRef<(() => void) | null>(null);
  const phaseRef = useRef<Phase>('loading');
  const mediaRecoveryResumePhaseRef = useRef<Phase | null>(null);
  const intentionalMediaTeardownRef = useRef(false);
  const discardRecordingOnStopRef = useRef(false);
  const noSpeechPromptOnStopRef = useRef<{ questionId: number | null } | null>(null);
  const noSpeechTimeoutRef = useRef<number | null>(null);
  const noAnswerPromptCountsRef = useRef<Record<string, number>>({});
  const sttRecoveryCoordinatorRef = useRef(createInterviewSttRecoveryCoordinator());
  const questionDeliveryCoordinatorRef = useRef(createInterviewQuestionDeliveryCoordinator());
  const authoritativeQuestionIdentityRef = useRef<string | null>(null);
  const deliveredQuestionIdentityRef = useRef<string | null>(null);
  const deliveredQuestionTimingRef = useRef<{ identity: string; completedAt: number } | null>(null);
  const vadMeasurementSequenceRef = useRef(0);
  const activeVadMeasurementRef = useRef<ActiveVadMeasurement | null>(null);
  const technicalInterruptionPromiseRef = useRef<Promise<void> | null>(null);
  const technicalInterruptionHandlerRef = useRef<(
    reasonCode: InterviewTechnicalInterruptionReasonCode,
    checkpoint: InterviewTechnicalInterruptionCheckpoint,
  ) => Promise<void>>(async () => undefined);
  const sttRecoveryCleanupTimerRef = useRef<number | null>(null);
  const submitAttemptCoordinatorRef = useRef(createSubmitAttemptCoordinator());
  const pendingSubmitRetryRef = useRef<{
    attemptId: string;
    retry: () => Promise<void>;
  } | null>(null);
  const submitRetryLockRef = useRef(false);
  const restoredSubmitAppliedRef = useRef<Set<string>>(new Set());
  const startTimeRef = useRef<number>(Date.now());
  const sessionStartRef = useRef<number>(Date.now());
  const backgroundSaveCountRef = useRef(0);
  const backgroundSaveRecordersRef = useRef<Set<MediaRecorder>>(new Set());
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const askedQuestionIdsRef = useRef<Set<string>>(new Set());
  const personalizedIntroPlaybackGuardRef = useRef(createPersonalizedIntroPlaybackGuard());
  const personalizedIntroTextRef = useRef('');
  const resumeContinuationPendingRef = useRef(false);
  const candidateQuestionTextsRef = useRef<Record<string, string[]>>({});
  const finishRequestedRef = useRef(false);
  const finishRetryTimerRef = useRef<number | null>(null);
  const finishCompletionCoordinatorRef = useRef(createFinalizationCompletionCoordinator());
  const sessionDisposingRef = useRef(false);
  const mediaRequestIdRef = useRef(0);
  const mediaInitializationPromiseRef = useRef<Promise<boolean> | null>(null);
  const pendingMediaSetupRef = useRef<PendingMediaSetup | null>(null);
  const mediaEffectCleanupTimerRef = useRef<number | null>(null);
  const shouldConfirmSessionExitRef = useRef(true);
  const allowSessionExitRef = useRef(false);
  const exitAfterConfirmRef = useRef<(() => void) | null>(null);
  const exitAfterCancelRef = useRef<(() => void) | null>(null);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);
  const ttsObjectUrlRef = useRef<string | null>(null);
  const ttsRequestIdRef = useRef(0);
  const ttsPendingRejectRef = useRef<((error: Error) => void) | null>(null);
  const transientDeliverySequenceRef = useRef(0);
  const mediaRecoveryStartedAtRef = useRef<number | null>(null);
  const mediaRecoveryTimerRef = useRef<number | null>(null);
  const jobDetailRef = useRef<InterviewJobDetail | null>(state?.job || null);
  const recentTemiUtterancesRef = useRef<string[]>([]);
  const silenceMonitorRef = useRef<{
    context: AudioContext | null;
    source: MediaStreamAudioSourceNode | null;
    analyser: AnalyserNode | null;
    buffer: Uint8Array | null;
    rafId: number | null;
    policyState: InterviewVadPolicyState;
    ambientState: InterviewVadAmbientState;
    terminationDispatched: boolean;
  } | null>(null);

  const stopSilenceMonitor = useCallback(() => {
    const monitor = silenceMonitorRef.current;
    if (!monitor) return;

    if (monitor.rafId !== null) {
      cancelAnimationFrame(monitor.rafId);
    }

    try { monitor.source?.disconnect(); } catch (_) {}
    try { monitor.analyser?.disconnect(); } catch (_) {}
    try { monitor.context?.close(); } catch (_) {}

    silenceMonitorRef.current = null;
  }, []);

  const emitVadMeasurementMetricOnce = useCallback((
    measurement: ActiveVadMeasurement,
    metricKey: string,
    event: string,
    payload: Record<string, any> = {},
    durationMs?: number | null,
  ) => {
    if (measurement.emittedMetrics.has(metricKey)) return;
    measurement.emittedMetrics.add(metricKey);
    trackInterviewMetric(event, {
      traceId: measurement.traceId,
      interviewId,
      questionId: measurement.questionId,
      recoveryId: measurement.identity,
      ...payload,
    }, durationMs);
  }, [interviewId]);

  const maybeEmitRecorderStartToFirstSpeech = useCallback((measurement: ActiveVadMeasurement) => {
    const durationMs = measuredDurationBetween(measurement.recorderStartedAt, measurement.firstSpeechAt);
    if (durationMs === null) return;
    emitVadMeasurementMetricOnce(
      measurement,
      'recorder_start_to_first_speech',
      'turn_recorder_start_to_first_speech_ms',
      { outcome: durationMs >= 0 ? 'measured' : 'speech_preceded_recorder_start' },
      durationMs,
    );
  }, [emitVadMeasurementMetricOnce]);

  const emitVadAmbientProfileOnce = useCallback((
    measurement: ActiveVadMeasurement,
    capturedAt: number,
    speechStarted: boolean,
  ) => {
    const monitor = silenceMonitorRef.current;
    if (!monitor) return;

    const frozen = freezeInterviewVadAmbientProfile(monitor.ambientState, {
      timestampMs: capturedAt,
      speechStarted,
    });
    monitor.ambientState = frozen.state;
    const profile = frozen.profile;
    emitVadMeasurementMetricOnce(
      measurement,
      'vad_ambient_profile',
      'turn_vad_ambient_profile',
      {
        stage: 'pre_speech_characterization',
        outcome: profile.sufficientSamples ? 'sufficient' : 'insufficient',
        sampleCount: profile.sampleCount,
        minRms: profile.minRms,
        avgRms: profile.averageRms,
        medianRms: profile.medianRms,
        p90Rms: profile.p90Rms,
        maxRms: profile.maxRms,
        startThreshold: profile.startThreshold,
        continuationThreshold: profile.continuationThreshold,
        startMarginOverP90: profile.startMarginOverP90,
        sufficientSamples: profile.sufficientSamples,
        speechStarted: profile.speechStarted,
      },
      profile.durationMs,
    );
  }, [emitVadMeasurementMetricOnce]);

  const captureVadSummary = useCallback((
    measurement: ActiveVadMeasurement,
    capturedAt: number,
  ): VadSummarySnapshot => {
    const monitor = silenceMonitorRef.current;
    const recordingDurationMs = measuredDurationBetween(measurement.recorderStartedAt, capturedAt);
    if (!monitor) {
      return measurement.summarySnapshot || {
        recordingDurationMs,
        peakRms: null,
        averageRms: null,
        speechDurationMs: null,
        strongSpeechDurationMs: null,
        speechRatio: null,
        speechStarted: false,
        vadSampleCount: 0,
      };
    }

    const policyState = monitor.policyState;
    const monitoredDurationMs = Math.max(1, capturedAt - policyState.createdAt);
    const speechDurationMs = Number(policyState.cumulativeSpeechMs) || 0;
    return {
      recordingDurationMs,
      peakRms: roundVadMeasurement(policyState.peakRms),
      averageRms: roundVadMeasurement(policyState.averageRms),
      speechDurationMs: roundVadMeasurement(speechDurationMs, 3),
      strongSpeechDurationMs: roundVadMeasurement(Number(policyState.cumulativeStrongSpeechMs) || 0, 3),
      speechRatio: roundVadMeasurement(speechDurationMs / monitoredDurationMs),
      speechStarted: Boolean(policyState.speechStarted),
      vadSampleCount: Number(policyState.sampleCount) || 0,
    };
  }, []);

  const emitVadSummaryOnce = useCallback((
    measurement: ActiveVadMeasurement,
    capturedAt: number,
  ) => {
    if (measurement.summaryEmitted) return;
    measurement.summarySnapshot = captureVadSummary(measurement, capturedAt);
    measurement.summaryEmitted = true;
    const summary = measurement.summarySnapshot;
    emitVadMeasurementMetricOnce(measurement, 'vad_summary', 'turn_vad_summary', {
      stage: 'recording_terminated',
      outcome: 'captured',
      recordingDurationMs: summary.recordingDurationMs,
      peakRms: summary.peakRms,
      averageRms: summary.averageRms,
      speechDurationMs: summary.speechDurationMs,
      strongSpeechDurationMs: summary.strongSpeechDurationMs,
      speechRatio: summary.speechRatio,
      speechStarted: summary.speechStarted,
      vadSampleCount: summary.vadSampleCount,
    });
  }, [captureVadSummary, emitVadMeasurementMetricOnce]);

  const markActiveVadTermination = useCallback((
    reason: VadTerminationReason,
    decidedAt = interviewPerformanceNow(),
  ) => {
    const measurement = activeVadMeasurementRef.current;
    if (!measurement) return null;
    if (measurement.terminationReason) return measurement.vadDecisionAt;

    measurement.terminationReason = reason;
    measurement.vadDecisionAt = decidedAt;
    emitVadAmbientProfileOnce(
      measurement,
      decidedAt,
      Boolean(silenceMonitorRef.current?.policyState.speechStarted),
    );
    emitVadMeasurementMetricOnce(measurement, 'vad_termination', 'turn_vad_termination', {
      stage: 'recording_termination',
      outcome: 'decided',
      vadReason: reason,
    });

    if (['end_silence', 'clear_speech_timeout', 'hard_max_duration'].includes(reason)) {
      const lastSpeechDurationMs = measuredDurationBetween(measurement.lastSpeechAt, decidedAt);
      if (lastSpeechDurationMs !== null) {
        emitVadMeasurementMetricOnce(
          measurement,
          'last_speech_to_vad_decision',
          'turn_last_speech_to_vad_decision_ms',
          { outcome: 'measured' },
          lastSpeechDurationMs,
        );
      }
    }

    emitVadSummaryOnce(measurement, decidedAt);
    return decidedAt;
  }, [emitVadAmbientProfileOnce, emitVadMeasurementMetricOnce, emitVadSummaryOnce]);

  const markActiveRecorderStopRequest = useCallback((
    recorder: MediaRecorder | null,
    requestedAt = interviewPerformanceNow(),
  ) => {
    const measurement = activeVadMeasurementRef.current;
    if (!measurement || (measurement.recorder && recorder && measurement.recorder !== recorder)) return;
    if (measurement.stopRequestedAt !== null) return;

    measurement.stopRequestedAt = requestedAt;
    const durationMs = measuredDurationBetween(measurement.vadDecisionAt, requestedAt);
    if (durationMs !== null) {
      emitVadMeasurementMetricOnce(
        measurement,
        'vad_decision_to_stop_request',
        'turn_vad_decision_to_stop_request_ms',
        { outcome: 'stop_requested' },
        durationMs,
      );
    }
  }, [emitVadMeasurementMetricOnce]);

  const markVadBlobReady = useCallback((
    measurement: ActiveVadMeasurement,
    blobReadyAt: number,
  ) => {
    if (measurement.blobReadyAt !== null) return;
    measurement.blobReadyAt = blobReadyAt;

    const decisionDurationMs = measuredDurationBetween(measurement.vadDecisionAt, blobReadyAt);
    if (decisionDurationMs !== null) {
      emitVadMeasurementMetricOnce(
        measurement,
        'vad_decision_to_blob_ready',
        'turn_vad_decision_to_blob_ready_ms',
        { stage: 'recorder_blob_ready', outcome: 'ready' },
        decisionDurationMs,
      );
    }

    const stopDurationMs = measuredDurationBetween(measurement.stopRequestedAt, blobReadyAt);
    if (stopDurationMs !== null) {
      emitVadMeasurementMetricOnce(
        measurement,
        'stop_request_to_blob_ready',
        'turn_stop_request_to_blob_ready_ms',
        { stage: 'recorder_blob_ready', outcome: 'ready' },
        stopDurationMs,
      );
    }
  }, [emitVadMeasurementMetricOnce]);

  const markVadSttStart = useCallback((
    measurement: ActiveVadMeasurement,
    startedAt: number,
  ) => {
    if (measurement.sttStartedAt !== null) return;
    measurement.sttStartedAt = startedAt;
    const durationMs = measuredDurationBetween(measurement.blobReadyAt, startedAt);
    if (durationMs === null) return;
    emitVadMeasurementMetricOnce(
      measurement,
      'blob_ready_to_stt_start',
      'turn_blob_ready_to_stt_start_ms',
      { stage: 'stt_operation_invoked', outcome: 'started' },
      durationMs,
    );
  }, [emitVadMeasurementMetricOnce]);

  const clearNoSpeechPromptTimer = useCallback(() => {
    if (noSpeechTimeoutRef.current !== null) {
      window.clearTimeout(noSpeechTimeoutRef.current);
      noSpeechTimeoutRef.current = null;
    }
  }, []);

  const rememberTemiUtterance = useCallback((text: string) => {
    const safeText = String(text || '').trim();
    if (!safeText) return;

    recentTemiUtterancesRef.current = [
      ...recentTemiUtterancesRef.current,
      safeText,
    ].slice(-32);
  }, []);

  const appendConversationTurn = useCallback((turn: Omit<ConversationTurn, 'id' | 'createdAt'> & { id?: string }) => {
    const id = turn.id || `${turn.speaker}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const nextTurn: ConversationTurn = {
      ...turn,
      id,
      createdAt: Date.now(),
    };

    setConversationTurns((previousTurns) => [...previousTurns, nextTurn].slice(-48));
    return id;
  }, []);

  const updateConversationTurn = useCallback((turnId: string, patch: Partial<ConversationTurn>) => {
    setConversationTurns((previousTurns) => previousTurns.map((turn) => (
      turn.id === turnId ? { ...turn, ...patch } : turn
    )));
  }, []);

  const removeConversationTurn = useCallback((turnId: string) => {
    setConversationTurns((previousTurns) => previousTurns.filter((turn) => turn.id !== turnId));
  }, []);

  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversationTurns, isTranscribing, isUserSpeaking, phase]);

  const cleanupElevenLabsAudio = useCallback(() => {
    const activeAudio = ttsAudioRef.current;
    if (activeAudio) {
      activeAudio.onplay = null;
      activeAudio.onended = null;
      activeAudio.onerror = null;
      try { activeAudio.pause(); } catch (_) {}
      try {
        activeAudio.removeAttribute('src');
        activeAudio.load();
      } catch (_) {}
    }

    ttsAudioRef.current = null;
    if (ttsObjectUrlRef.current) {
      URL.revokeObjectURL(ttsObjectUrlRef.current);
      ttsObjectUrlRef.current = null;
    }
  }, []);

  const stopTtsPlayback = useCallback((options?: { updateSpeaking?: boolean }) => {
    ttsRequestIdRef.current += 1;
    const pendingReject = ttsPendingRejectRef.current;
    ttsPendingRejectRef.current = null;
    if (pendingReject) {
      const cancellation = new Error('Speech delivery cancelled');
      cancellation.name = 'SpeechDeliveryCancelledError';
      pendingReject(cancellation);
    }
    window.speechSynthesis?.cancel();
    cleanupElevenLabsAudio();
    if (options?.updateSpeaking !== false) {
      setAiSpeaking(false);
    }
  }, [cleanupElevenLabsAudio]);

  useEffect(() => {
    phaseRef.current = phase;
    shouldConfirmSessionExitRef.current = phase !== 'done' && phase !== 'finishing' && !finishRequestedRef.current;
  }, [phase]);

  const beginBackgroundSave = useCallback(() => {
    backgroundSaveCountRef.current += 1;
    setBackgroundSaveCount(backgroundSaveCountRef.current);
  }, []);

  const finishBackgroundSave = useCallback(() => {
    backgroundSaveCountRef.current = Math.max(0, backgroundSaveCountRef.current - 1);
    setBackgroundSaveCount(backgroundSaveCountRef.current);
  }, []);

  const resetRecordedAnswerState = useCallback(() => {
    pendingSubmitRetryRef.current = null;
    submitRetryLockRef.current = false;
    setPendingSubmitAttemptId(null);
    setIsRetryingSubmit(false);
    setSubmitActivity('idle');
    setAnswer('');
    setSttRecoveryPhase('idle');
    setSttTerminalFailure(false);
    setTranscribeError(false);
    setIsUserSpeaking(false);
    setTranscriptQuality(null);
    setTranscriptMode('');
    setTranscriptNotice('');
    setSubmitError('');
  }, []);

  const teardownMediaStream = useCallback((options?: { suppressOnEnded?: boolean }) => {
    const activeStream = streamRef.current;
    if (!activeStream) {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    if (options?.suppressOnEnded) {
      intentionalMediaTeardownRef.current = true;
    }

    activeStream.getTracks().forEach((track) => {
      track.onended = null;
      try { track.stop(); } catch (_) {}
    });

    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (options?.suppressOnEnded) {
      window.setTimeout(() => {
        intentionalMediaTeardownRef.current = false;
      }, 0);
    }
  }, []);

  const shutdownSessionMedia = useCallback((
    terminationReason: VadTerminationReason = 'deliberate_termination',
  ) => {
    sessionDisposingRef.current = true;
    mediaRequestIdRef.current += 1;
    const pendingMediaSetup = pendingMediaSetupRef.current;
    if (pendingMediaSetup && !pendingMediaSetup.completed) {
      pendingMediaSetup.completed = true;
      trackInterviewDuration('media_setup_ms', pendingMediaSetup.startedAt, {
        traceId: startupTraceIdRef.current,
        interviewId,
        outcome: 'cancelled',
      });
      pendingMediaSetupRef.current = null;
    }
    clearNoSpeechPromptTimer();
    noSpeechPromptOnStopRef.current = null;
    const activeAudioRecorder = recRef.current as MediaRecorder | null;
    if (activeAudioRecorder && activeAudioRecorder.state !== 'inactive') {
      markActiveVadTermination(terminationReason);
    }
    stopSilenceMonitor();
    questionDeliveryCoordinatorRef.current.cancel();
    stopTtsPlayback({ updateSpeaking: false });

    if (audioStopTimeoutRef.current !== null) {
      window.clearTimeout(audioStopTimeoutRef.current);
      audioStopTimeoutRef.current = null;
    }
    audioStopFallbackRef.current = null;

    const activeRecorders = [
      recRef.current as MediaRecorder | null,
      videoRecRef.current as MediaRecorder | null,
    ];
    const hasActiveRecorder = activeRecorders.some((recorder) => recorder && recorder.state !== 'inactive');
    if (hasActiveRecorder) {
      discardRecordingOnStopRef.current = true;
    }

    activeRecorders.forEach((recorder) => {
      if (!recorder) return;
      try {
        if (recorder.state !== 'inactive') {
          if (recorder === recRef.current) {
            markActiveRecorderStopRequest(recorder);
          }
          recorder.stop();
        }
      } catch (_) {}
    });

    recRef.current = null;
    videoRecRef.current = null;
    recorderStartLockRef.current = false;
    teardownMediaStream({ suppressOnEnded: true });
  }, [
    clearNoSpeechPromptTimer,
    interviewId,
    markActiveRecorderStopRequest,
    markActiveVadTermination,
    stopSilenceMonitor,
    stopTtsPlayback,
    teardownMediaStream,
  ]);

  const handleTechnicalInterruption = useCallback((
    reasonCode: InterviewTechnicalInterruptionReasonCode,
    checkpoint: InterviewTechnicalInterruptionCheckpoint,
  ) => {
    if (technicalInterruptionPromiseRef.current) return technicalInterruptionPromiseRef.current;

    const operation = (async () => {
      const currentUserId = readAuthUserId();
      const interruptionContext = {
        traceId: activeTurnTimingRef.current?.traceId || startupTraceIdRef.current,
        interviewId,
        questionId: activeTurnTimingRef.current?.questionId || null,
        clientAttemptId: activeTurnTimingRef.current?.clientAttemptId || null,
      };
      trackInterviewMetric('interview_technical_interruption_started', {
        ...interruptionContext,
        reasonCode,
        checkpoint,
        outcome: 'started',
      });

      if (currentUserId) {
        persistInterviewTechnicalInterruptionIntent({
          userId: currentUserId,
          interviewId,
          reasonCode,
          checkpoint,
        });
      }

      deliveredQuestionIdentityRef.current = null;
      authoritativeQuestionIdentityRef.current = null;
      allowSessionExitRef.current = true;
      shouldConfirmSessionExitRef.current = false;
      setTechnicalInterruptionMessage(
        'Interview dihentikan karena kendala teknis. Kendala ini tidak dianggap sebagai kegagalan interview kamu.'
      );
      setPhase('technical_interrupted');
      setIsTranscribing(false);
      setIsUserSpeaking(false);
      setSubmitActivity('idle');
      sttRecoveryCoordinatorRef.current.cancel();
      questionDeliveryCoordinatorRef.current.cancel();
      shutdownSessionMedia('technical_interruption');

      let canonicalAcknowledged = false;
      try {
        const response = await interviewService.technicalInterruption(
          interviewId,
          { reason_code: reasonCode, checkpoint },
          interruptionContext,
        );
        canonicalAcknowledged = response.data?.status === true
          && String(response.data?.data?.status || '').toLowerCase() === 'technical_interrupted';
        if (canonicalAcknowledged && currentUserId) {
          clearInterviewTechnicalInterruptionIntent(currentUserId, interviewId);
        }
        trackInterviewMetric('interview_technical_interruption_completed', {
          ...interruptionContext,
          reasonCode,
          checkpoint,
          httpStatus: Number(response.status) || null,
          outcome: canonicalAcknowledged ? 'acknowledged' : 'pending_reconciliation',
        });
      } catch (error: any) {
        const errorCode = String(error?.response?.data?.error_code || '').toUpperCase();
        if (currentUserId && ['INTERVIEW_TECHNICAL_INTERRUPTED', 'INTERVIEW_ALREADY_COMPLETED'].includes(errorCode)) {
          clearInterviewTechnicalInterruptionIntent(currentUserId, interviewId);
          canonicalAcknowledged = true;
        }
        trackInterviewMetric('interview_technical_interruption_failed', {
          ...interruptionContext,
          reasonCode,
          checkpoint,
          httpStatus: Number(error?.response?.status) || null,
          outcome: canonicalAcknowledged ? 'canonical_terminal' : 'stored_for_reconciliation',
        });
      } finally {
        pendingSubmitRetryRef.current = null;
        submitRetryLockRef.current = false;
        if (currentUserId) {
          clearPendingAnswerRecovery({ userId: currentUserId, interviewId });
        }
        resetInterview();
        window.setTimeout(() => navigate('/status-lamaran'), 1800);
      }
    })();

    technicalInterruptionPromiseRef.current = operation;
    return operation;
  }, [interviewId, navigate, resetInterview, shutdownSessionMedia]);

  technicalInterruptionHandlerRef.current = handleTechnicalInterruption;

  const requestSessionExit = useCallback((onConfirm: () => void, onCancel?: () => void) => {
    if (!shouldConfirmSessionExitRef.current || allowSessionExitRef.current) {
      allowSessionExitRef.current = true;
      shutdownSessionMedia();
      onConfirm();
      return;
    }

    exitAfterConfirmRef.current = onConfirm;
    exitAfterCancelRef.current = onCancel || null;
    setExitConfirmOpen(true);
  }, [shutdownSessionMedia]);

  const confirmSessionExit = useCallback(() => {
    exitAfterConfirmRef.current = null;
    exitAfterCancelRef.current = null;
    allowSessionExitRef.current = true;
    setExitConfirmOpen(false);
    shutdownSessionMedia();
    navigate('/status-lamaran');
  }, [navigate, shutdownSessionMedia]);

  const cancelSessionExit = useCallback(() => {
    const cancelAction = exitAfterCancelRef.current;
    exitAfterConfirmRef.current = null;
    exitAfterCancelRef.current = null;
    setExitConfirmOpen(false);
    cancelAction?.();
  }, []);

  const handleMediaFailure = useCallback((nextState: 'blocked' | 'lost', message: string) => {
    const currentPhase = phaseRef.current;
    mediaRecoveryResumePhaseRef.current = currentPhase === 'greeting' || currentPhase === 'ai_speaking'
      ? 'ai_speaking'
      : currentPhase === 'responding'
      ? 'idle'
      : currentPhase === 'recording'
      ? 'idle'
      : currentPhase === 'review' || currentPhase === 'submitting'
      ? 'review'
      : currentPhase === 'idle'
      ? 'idle'
      : null;

    stopTtsPlayback();
    clearNoSpeechPromptTimer();
    noSpeechPromptOnStopRef.current = null;
    setCameraReady(false);
    setCameraError(message);
    setMediaAvailability(nextState);

    if (currentPhase === 'recording') {
      discardRecordingOnStopRef.current = true;
      markActiveVadTermination('media_loss');
      stopSilenceMonitor();
      resetRecordedAnswerState();
      setIsTranscribing(false);
      recorderStartLockRef.current = false;

      try {
        if (recRef.current?.state === 'recording') {
          markActiveRecorderStopRequest(recRef.current);
          recRef.current.stop();
        }
      } catch (_) {}

      try {
        if (videoRecRef.current?.state === 'recording') {
          videoRecRef.current.stop();
        }
      } catch (_) {}

      setPhase('idle');
      return;
    }

    if (currentPhase === 'greeting') {
      setPhase('idle');
      return;
    }

    if (currentPhase === 'ai_speaking' || currentPhase === 'responding') {
      setPhase('idle');
      return;
    }

    if (currentPhase === 'submitting') {
      setPhase('review');
    }
  }, [
    clearNoSpeechPromptTimer,
    markActiveRecorderStopRequest,
    markActiveVadTermination,
    resetRecordedAnswerState,
    stopSilenceMonitor,
    stopTtsPlayback,
  ]);

  const completePendingMediaSetup = useCallback((setup: PendingMediaSetup, outcome: string) => {
    if (setup.completed) return;
    setup.completed = true;
    trackInterviewDuration('media_setup_ms', setup.startedAt, {
      traceId: startupTraceIdRef.current,
      interviewId,
      outcome,
    });
    if (pendingMediaSetupRef.current === setup) {
      pendingMediaSetupRef.current = null;
    }
  }, [interviewId]);

  const adoptSessionMediaStream = useCallback((stream: MediaStream) => {
    streamRef.current = stream;
    stream.getTracks().forEach((track) => {
      track.onended = () => {
        if (intentionalMediaTeardownRef.current) return;
        handleMediaFailure(
          'lost',
          track.kind === 'video'
            ? 'Kamera terputus. Aktifkan kembali kamera lalu coba lagi.'
            : 'Mikrofon terputus. Aktifkan kembali mikrofon lalu coba lagi.'
        );
      };
    });
  }, [handleMediaFailure]);

  const attachCurrentMediaStream = useCallback(async () => {
    const setup = pendingMediaSetupRef.current;
    const videoElement = videoRef.current;
    let stream = streamRef.current;
    if (!setup || setup.completed || setup.attaching || !videoElement || !stream) return false;

    setup.attaching = true;
    try {
      let attachmentStatus = await attachInterviewMediaStream(videoElement, stream);
      if (sessionDisposingRef.current || setup.requestId !== mediaRequestIdRef.current) return false;

      if (attachmentStatus !== 'attached' && setup.reusedHandoff) {
        if (videoElement.srcObject === stream) videoElement.srcObject = null;
        stopInterviewMediaStream(stream);
        if (streamRef.current === stream) streamRef.current = null;

        stream = await navigator.mediaDevices.getUserMedia(mediaCaptureConstraints);
        if (sessionDisposingRef.current || setup.requestId !== mediaRequestIdRef.current) {
          stopInterviewMediaStream(stream);
          return false;
        }
        if (!isInterviewMediaStreamUsable(stream)) {
          stopInterviewMediaStream(stream);
          throw new Error('Reacquired media stream is not usable');
        }

        setup.reusedHandoff = false;
        setup.outcome = attachmentStatus === 'playback_failed'
          ? 'handoff_playback_failed_reacquired'
          : 'handoff_invalid_reacquired';
        adoptSessionMediaStream(stream);
        attachmentStatus = await attachInterviewMediaStream(videoElement, stream);
      }

      if (
        attachmentStatus !== 'attached'
        || !isInterviewMediaStreamUsable(stream)
        || sessionDisposingRef.current
        || setup.requestId !== mediaRequestIdRef.current
      ) {
        throw new Error(`Session media attachment failed: ${attachmentStatus}`);
      }

      setCameraReady(true);
      setCameraError('');
      setMediaAvailability('ready');

      const recoveryPhase = mediaRecoveryResumePhaseRef.current;
      mediaRecoveryResumePhaseRef.current = null;
      if (recoveryPhase === 'ai_speaking') {
        setPhase('ai_speaking');
      }

      completePendingMediaSetup(setup, setup.outcome);
      return true;
    } catch (_) {
      if (videoElement.srcObject === stream) videoElement.srcObject = null;
      if (streamRef.current === stream) streamRef.current = null;
      stopInterviewMediaStream(stream);
      setCameraReady(false);
      setCameraError('Kamera aktif tetapi tampilan video belum tersambung. TEMI akan mencoba menyambungkan ulang otomatis.');
      setMediaAvailability('blocked');
      completePendingMediaSetup(setup, `${setup.outcome}_failed`);
      return false;
    } finally {
      setup.attaching = false;
    }
  }, [adoptSessionMediaStream, completePendingMediaSetup, mediaCaptureConstraints]);

  const initializeMediaAccess = useCallback(() => {
    if (mediaInitializationPromiseRef.current) {
      return mediaInitializationPromiseRef.current;
    }

    const task = (async () => {
      const mediaSetupStartedAt = interviewPerformanceNow();
      const requestId = mediaRequestIdRef.current + 1;
      mediaRequestIdRef.current = requestId;
      sessionDisposingRef.current = false;

      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraReady(false);
        setCameraError('Browser tidak mendukung kamera/mikrofon atau halaman perlu dibuka via HTTPS.');
        setMediaAvailability('blocked');
        trackInterviewDuration('media_setup_ms', mediaSetupStartedAt, {
          traceId: startupTraceIdRef.current,
          interviewId,
          outcome: 'unsupported',
        });
        return false;
      }

      teardownMediaStream({ suppressOnEnded: true });
      pendingMediaSetupRef.current = null;
      setCameraReady(false);
      setCameraError('');
      setMediaAvailability('loading');

      try {
        const shouldReusePreparedMedia = reusePreparedMediaRef.current;
        reusePreparedMediaRef.current = false;
        const acquisition = await acquireInterviewMediaStream({
          reusePreparedMedia: shouldReusePreparedMedia,
          reacquire: () => navigator.mediaDevices.getUserMedia(mediaCaptureConstraints),
        });
        const stream = acquisition.stream;
        if (!stream) throw new Error('Acquired media stream is not usable');

        if (sessionDisposingRef.current || requestId !== mediaRequestIdRef.current) {
          stopInterviewMediaStream(stream);
          trackInterviewDuration('media_setup_ms', mediaSetupStartedAt, {
            traceId: startupTraceIdRef.current,
            interviewId,
            outcome: 'cancelled',
          });
          return false;
        }

        const setup: PendingMediaSetup = {
          requestId,
          startedAt: mediaSetupStartedAt,
          outcome: acquisition.outcome,
          reusedHandoff: acquisition.reusedHandoff,
          attaching: false,
          completed: false,
        };
        pendingMediaSetupRef.current = setup;
        adoptSessionMediaStream(stream);
        setMediaStreamRevision((current) => current + 1);
        return true;
      } catch (error: any) {
        setCameraReady(false);
        setCameraError(resolveMediaAccessErrorMessage(error));
        setMediaAvailability('blocked');
        trackInterviewDuration('media_setup_ms', mediaSetupStartedAt, {
          traceId: startupTraceIdRef.current,
          interviewId,
          outcome: 'blocked',
        });
        return false;
      }
    })();

    mediaInitializationPromiseRef.current = task;
    const release = () => {
      if (mediaInitializationPromiseRef.current === task) {
        mediaInitializationPromiseRef.current = null;
      }
    };
    task.then(release, release);
    return task;
  }, [adoptSessionMediaStream, interviewId, mediaCaptureConstraints, teardownMediaStream]);

  useEffect(() => {
    const mediaUnavailable = mediaAvailability === 'blocked' || mediaAvailability === 'lost';
    if (!mediaUnavailable || phase === 'done' || phase === 'finishing' || phase === 'technical_interrupted') {
      if (mediaAvailability === 'ready') mediaRecoveryStartedAtRef.current = null;
      if (mediaRecoveryTimerRef.current !== null) {
        window.clearTimeout(mediaRecoveryTimerRef.current);
        mediaRecoveryTimerRef.current = null;
      }
      return undefined;
    }

    if (mediaRecoveryStartedAtRef.current === null) {
      mediaRecoveryStartedAtRef.current = interviewPerformanceNow();
    }
    const elapsed = interviewPerformanceNow() - mediaRecoveryStartedAtRef.current;
    if (elapsed >= 30000) {
      void handleTechnicalInterruption(
        'MEDIA_ACCESS_EXHAUSTED',
        deliveredQuestionIdentityRef.current ? 'QUESTION_DELIVERED' : 'QUESTION_AUTHORITATIVE',
      );
      return undefined;
    }

    mediaRecoveryTimerRef.current = window.setTimeout(() => {
      mediaRecoveryTimerRef.current = null;
      setSubmitError('');
      void initializeMediaAccess();
    }, Math.min(2000, Math.max(1, 30000 - elapsed)));

    return () => {
      if (mediaRecoveryTimerRef.current !== null) {
        window.clearTimeout(mediaRecoveryTimerRef.current);
        mediaRecoveryTimerRef.current = null;
      }
    };
  }, [handleTechnicalInterruption, initializeMediaAccess, mediaAvailability, phase]);

  const initialRouteSessionMode = initialStartupNavigation.sessionMode;

  const startSilenceMonitor = useCallback((
    audioStream: MediaStream,
    options: {
      autoStop?: boolean;
      onSpeechStart?: () => void;
      onSpeechPause?: () => void;
      onSpeechEnd?: (reason: VadTerminationReason, decidedAt: number) => void;
    } = {}
  ) => {
    stopSilenceMonitor();

    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;

    try {
      const context: AudioContext = new AudioContextCtor();
      const source = context.createMediaStreamSource(audioStream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      const buffer = new Uint8Array(analyser.fftSize);
      const monitorCreatedAt = performance.now();
      const policyState = createInterviewVadPolicyState(monitorCreatedAt);
      const ambientState = createInterviewVadAmbientState(monitorCreatedAt);
      const monitor = {
        context,
        source,
        analyser,
        buffer,
        rafId: null as number | null,
        policyState,
        ambientState,
        terminationDispatched: false,
      };

      const requestStop = (reason: VadTerminationReason, decidedAt: number) => {
        if (!options.autoStop || monitor.terminationDispatched) return;
        monitor.terminationDispatched = true;
        markActiveVadTermination(reason, decidedAt);
        options.onSpeechEnd?.(reason, decidedAt);
      };

      const sample = () => {
        if (silenceMonitorRef.current !== monitor) return;
        analyser.getByteTimeDomainData(buffer);

        let sum = 0;
        for (let i = 0; i < buffer.length; i += 1) {
          const centered = (buffer[i] - 128) / 128;
          sum += centered * centered;
        }

        const rms = Math.sqrt(sum / buffer.length);
        const now = performance.now();
        const transition = consumeInterviewVadSample(monitor.policyState, {
          rms,
          timestampMs: now,
          terminationEnabled: Boolean(options.autoStop),
        });
        monitor.policyState = transition.state;
        const speechStartedEvent = transition.events.find((event) => event.type === 'speech_started');
        if (speechStartedEvent) {
          const measurement = activeVadMeasurementRef.current;
          if (measurement) {
            emitVadAmbientProfileOnce(measurement, speechStartedEvent.timestampMs, true);
          }
        } else if (!transition.state.speechStarted) {
          monitor.ambientState = observeInterviewVadAmbientSample(monitor.ambientState, {
            rms,
            timestampMs: now,
          });
        }

        if (transition.sample.isQualifyingSpeech) {
          const measurement = activeVadMeasurementRef.current;
          if (measurement) {
            measurement.lastSpeechAt = now;
          }
          if (transition.sample.isStrongSpeech) {
            if (
              measurement
              && measurement.recorderStartedAt !== null
              && measurement.firstSpeechAt === null
            ) {
              measurement.firstSpeechAt = now;
              maybeEmitRecorderStartToFirstSpeech(measurement);
            }
          }
        }

        for (const event of transition.events) {
          if (event.type === 'speech_started' || event.type === 'speech_continued') {
            options.onSpeechStart?.();
          } else if (event.type === 'speech_paused') {
            options.onSpeechPause?.();
          } else if (event.type === 'terminate') {
            requestStop(event.reason, event.timestampMs);
          }
        }

        monitor.rafId = window.requestAnimationFrame(sample);
      };

      monitor.rafId = window.requestAnimationFrame(sample);
      silenceMonitorRef.current = monitor;
    } catch (_) {
      silenceMonitorRef.current = null;
    }
  }, [
    emitVadAmbientProfileOnce,
    markActiveVadTermination,
    maybeEmitRecorderStartToFirstSpeech,
    stopSilenceMonitor,
  ]);

  const isLikelySilentRecording = useCallback((blobSize: number) => {
    const monitor = silenceMonitorRef.current;
    const policyState = monitor?.policyState || null;
    return isInterviewVadRecordingLikelySilent({
      state: policyState,
      recordingDurationMs: policyState
        ? Math.max(1, performance.now() - policyState.createdAt)
        : 0,
      blobSize,
    });
  }, []);

  // ── Camera init ──────────────────────────────────────────────
  useEffect(() => {
    if (mediaEffectCleanupTimerRef.current !== null) {
      window.clearTimeout(mediaEffectCleanupTimerRef.current);
      mediaEffectCleanupTimerRef.current = null;
    }

    if (!isInterviewMediaStreamUsable(streamRef.current)) {
      void initializeMediaAccess();
    }

    return () => {
      mediaEffectCleanupTimerRef.current = window.setTimeout(() => {
        mediaEffectCleanupTimerRef.current = null;
        shutdownSessionMedia();
      }, 0);
    };
  }, [initializeMediaAccess, shutdownSessionMedia]);

  useEffect(() => {
    const recoveryCoordinator = sttRecoveryCoordinatorRef.current;
    if (sttRecoveryCleanupTimerRef.current !== null) {
      window.clearTimeout(sttRecoveryCleanupTimerRef.current);
      sttRecoveryCleanupTimerRef.current = null;
    }

    return () => {
      // The zero-delay guard prevents React.StrictMode's development-only
      // setup/cleanup replay from cancelling the single live recovery chain.
      sttRecoveryCleanupTimerRef.current = window.setTimeout(() => {
        sttRecoveryCleanupTimerRef.current = null;
        recoveryCoordinator.cancel();
      }, 0);
    };
  }, []);

  useEffect(() => {
    void attachCurrentMediaStream();
  }, [attachCurrentMediaStream, mediaStreamRevision, phase]);

  useEffect(() => {
    const confirmMessage = 'Interview masih berlangsung. Jika keluar sekarang, kamera dan mikrofon akan dimatikan dan jawaban yang belum tersimpan bisa hilang.';

    const handlePageExit = () => {
      shutdownSessionMedia();
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldConfirmSessionExitRef.current || allowSessionExitRef.current) return undefined;
      event.preventDefault();
      event.returnValue = confirmMessage;
      return confirmMessage;
    };

    window.addEventListener('pagehide', handlePageExit);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', handlePageExit);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shutdownSessionMedia]);

  useEffect(() => {
    const armHistoryGuard = () => {
      try {
        window.history.pushState(
          { ...(window.history.state || {}), interviewSessionGuard: true },
          '',
          window.location.href
        );
      } catch (_) {}
    };

    armHistoryGuard();

    const handlePopState = () => {
      if (!shouldConfirmSessionExitRef.current || allowSessionExitRef.current) return;
      requestSessionExit(
        () => {
          window.setTimeout(() => window.history.back(), 0);
        },
        armHistoryGuard
      );
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [requestSessionExit]);

  useEffect(() => {
    if (!resumeNotice) return undefined;
    const timerId = window.setTimeout(() => setResumeNotice(''), 5000);
    return () => window.clearTimeout(timerId);
  }, [resumeNotice]);

  // ── Load interview data ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!interviewId || !outsourceIdVal || !jobIdNum) {
      navigate('/lowongan-kerja');
      return undefined;
    }

    const loadInterview = async () => {
      try {
        const startupContext = {
          traceId: startupTraceIdRef.current,
          interviewId,
        };
        const sessionRequestStartedAt = interviewPerformanceNow();
        let res;
        try {
          res = await interviewService.get(interviewId, startupContext);
          trackInterviewDuration('session_get_ms', sessionRequestStartedAt, {
            ...startupContext,
            outcome: 'success',
            httpStatus: Number(res.status) || null,
          });
        } catch (error: any) {
          trackInterviewDuration('session_get_ms', sessionRequestStartedAt, {
            ...startupContext,
            outcome: 'error',
            httpStatus: Number(error?.response?.status) || null,
          });
          throw error;
        }
        if (cancelled) return;
        const iv = res.data?.data;
        const id: number = iv?.interview?.id ?? interviewId;
        if (String(iv?.interview?.status || '').toLowerCase() === 'technical_interrupted') {
          const currentUserId = readAuthUserId();
          if (currentUserId) {
            clearPendingAnswerRecovery({ userId: currentUserId, interviewId: id });
            clearInterviewTechnicalInterruptionIntent(currentUserId, id);
          }
          allowSessionExitRef.current = true;
          shouldConfirmSessionExitRef.current = false;
          setTechnicalInterruptionMessage(
            'Interview ini telah dihentikan karena kendala teknis. Kendala ini tidak dianggap sebagai kegagalan interview kamu.'
          );
          setPhase('technical_interrupted');
          shutdownSessionMedia();
          resetInterview();
          window.setTimeout(() => navigate('/status-lamaran'), 1200);
          return;
        }
        const qs: InterviewQuestion[] = iv?.questions ?? [];
        const progress: InterviewProgressPayload = iv?.progress ?? null;
        const answeredQuestionIds = Array.isArray(progress?.answered_question_ids)
          ? progress.answered_question_ids
              .map((questionId) => Number(questionId))
              .filter((questionId) => Number.isFinite(questionId) && questionId > 0)
          : [];
        const currentQuestionId = Number(progress?.current_question_id);
        const answeredCount = Number.isFinite(Number(progress?.answered_count))
          ? Number(progress?.answered_count)
          : answeredQuestionIds.length;
        const canFinish = Boolean(progress?.can_finish) || (answeredCount >= qs.length && qs.length > 0);
        const currentUserId = readAuthUserId();
        let recoveryEnvelope = null;
        if (currentUserId) {
          clearPendingAnswerRecoveriesExcept({ userId: currentUserId, interviewId: id });
          recoveryEnvelope = readPendingAnswerRecovery({ userId: currentUserId, interviewId: id }).envelope;
        }
        const restoreDecision = recoveryEnvelope
          ? resolvePendingAnswerRestoreDecision(recoveryEnvelope, {
              questionIds: qs.map((question) => Number(question.id)),
              answeredQuestionIds,
              currentQuestionId: Number.isFinite(currentQuestionId) && currentQuestionId > 0
                ? currentQuestionId
                : null,
              interviewCompleted: iv?.interview?.status === 'completed',
              canFinish,
            })
          : null;
        const shouldReconcilePendingAnswer = restoreDecision?.action === 'reconcile';
        let detailPayload: any = null;

        if (!shouldReconcilePendingAnswer) {
          const detailRequestStartedAt = interviewPerformanceNow();
          try {
            const detailRes = await interviewService.getDetail(id, {
              ...startupContext,
              interviewId: id,
            });
            detailPayload = detailRes.data?.data || null;
            trackInterviewDuration('session_detail_ms', detailRequestStartedAt, {
              ...startupContext,
              interviewId: id,
              outcome: 'success',
              httpStatus: Number(detailRes.status) || null,
            });
          } catch (error: any) {
            detailPayload = null;
            trackInterviewDuration('session_detail_ms', detailRequestStartedAt, {
              ...startupContext,
              interviewId: id,
              outcome: 'error',
              httpStatus: Number(error?.response?.status) || null,
            });
          }
        }
        if (cancelled) return;

        if (qs.length) {
          const answeredQuestionIdSet = new Set(answeredQuestionIds);
          const explicitCurrentIndex = Number.isFinite(currentQuestionId) && currentQuestionId > 0
            ? qs.findIndex((question) => Number(question.id) === currentQuestionId)
            : -1;
          const fallbackCurrentIndex = qs.findIndex((question) => !answeredQuestionIdSet.has(Number(question.id)));
          const resolvedQuestionIndex = explicitCurrentIndex >= 0
            ? explicitCurrentIndex
            : fallbackCurrentIndex >= 0
            ? fallbackCurrentIndex
            : 0;
          const resolvedPosition = Number.isFinite(Number(progress?.current_question_position))
            ? Number(progress?.current_question_position)
            : resolvedQuestionIndex + 1;
          const effectiveSessionMode = resolveEffectiveInterviewSessionMode({
            initialRouteSessionMode,
            canonicalSessionMode: progress?.session_mode,
          });
          const startupOpening = resolveInterviewStartupOpening({
            canFinish,
            sessionMode: effectiveSessionMode,
            answeredCount,
            currentQuestionPosition: resolvedPosition,
            candidateName: String(state?.candidateName || ''),
          });
          const resolvedPhase: Phase = startupOpening.phase;
          const shouldUseResumeTransition = startupOpening.shouldUseResumeTransition;
          personalizedIntroTextRef.current = startupOpening.standaloneOpeningSegments[0] || '';
          const historicalAnswers = resolveHistoricalAnswers(
            detailPayload,
            hasScopedInterviewCache ? interviewData.answers : []
          );
          const historicalTurns = buildConversationTurnsFromHistory(qs, historicalAnswers);
          const recoveredQuestionIndex = recoveryEnvelope
            ? qs.findIndex((question) => Number(question.id) === Number(recoveryEnvelope?.question_id))
            : -1;

          if (recoveryEnvelope && !shouldReconcilePendingAnswer && currentUserId) {
            clearPendingAnswerRecovery({ userId: currentUserId, interviewId: id });
          }

          setLocalQuestions(qs);
          setQuestions(qs);
          setInterviewId(id);
          setJobId(jobIdNum);
          setOutsourceId(outsourceIdVal);
          setStatus(iv?.interview?.status === 'completed' ? 'completed' : 'in_progress');
          resetRecordedAnswerState();
          setConversationTurns(historicalTurns);
          historicalAnswers.forEach((answer: any) => {
            const questionId = Number(answer?.question_id);
            if (Number.isFinite(questionId) && questionId > 0) {
              askedQuestionIdsRef.current.add(String(questionId));
            }
            if (questionId && String(answer?.answer_text || '').trim()) {
              addAnswer({
                id: Number(answer?.id) || 0,
                question_id: questionId,
                answer_text: String(answer.answer_text || '').trim(),
                time_spent_seconds: Number(answer?.time_spent_seconds) || 0,
                score: Number(answer?.score) || 0,
                ai_feedback: String(answer?.ai_feedback || ''),
                needs_followup: Boolean(answer?.needs_followup),
                video_url: answer?.video_url ?? null,
                video_mime_type: answer?.video_mime_type ?? null,
                video_size_bytes: answer?.video_size_bytes ?? null,
                video_duration_seconds: answer?.video_duration_seconds ?? null,
              });
            }
          });
          setCurrentIdx(shouldReconcilePendingAnswer && recoveredQuestionIndex >= 0
            ? recoveredQuestionIndex
            : canFinish
            ? qs.length
            : resolvedQuestionIndex);
          resumeContinuationPendingRef.current = shouldReconcilePendingAnswer ? false : shouldUseResumeTransition;
          setPhase(shouldReconcilePendingAnswer ? 'submitting' : resolvedPhase);
          setSubmitActivity(shouldReconcilePendingAnswer ? 'reconciling' : 'idle');

          if (shouldReconcilePendingAnswer) {
            setResumeNotice('Memastikan jawaban yang tertunda sebelum melanjutkan interview.');
          } else if (canFinish) {
            setResumeNotice('Sesi dipulihkan. Semua jawaban tersimpan, lanjutkan untuk menyelesaikan interview.');
          } else if (shouldUseResumeTransition) {
            setResumeNotice(`Sesi dipulihkan. Lanjutkan dari pertanyaan ${resolvedPosition}.`);
          } else {
            setResumeNotice('');
          }

          if (!sessionInitLoggedRef.current) {
            sessionInitLoggedRef.current = true;
            trackInterviewDuration('session_init_ms', sessionInitStartedAtRef.current, {
              traceId: startupTraceIdRef.current,
              interviewId: id,
              sessionMode: effectiveSessionMode,
              outcome: 'ready',
            });
          }

          if (!shouldReconcilePendingAnswer || !recoveryEnvelope || !currentUserId) return;

          activeTurnTimingRef.current = {
            traceId: createInterviewTraceId('turn'),
            clientAttemptId: recoveryEnvelope.client_attempt_id,
            questionId: Number(recoveryEnvelope.question_id) || null,
            startedAt: interviewPerformanceNow(),
            firstAudioLogged: false,
            listeningLogged: false,
          };

          setAnswer(recoveryEnvelope.payload.answer_text);
          setTranscriptMode((recoveryEnvelope.payload.transcript_mode || '') as TranscriptMode);
          setPendingSubmitAttemptId(recoveryEnvelope.client_attempt_id);

          const recoveryKey = buildPendingAnswerRecoveryKey(currentUserId, id);
          if (!recoveryKey) {
            clearPendingAnswerRecovery({ userId: currentUserId, interviewId: id });
            setPendingSubmitAttemptId(null);
            setSubmitActivity('idle');
            setSubmitError('Data pemulihan jawaban tidak valid. TEMI akan melanjutkan dari pertanyaan yang sama.');
            resumeContinuationPendingRef.current = true;
            setPhase('ai_speaking');
            return;
          }

          const applyPostSubmitCanonicalState = (source: any, successNotice: string) => {
            const canonicalQuestions: InterviewQuestion[] = Array.isArray(source?.questions)
              ? source.questions
              : [];
            const canonicalProgress: InterviewProgressPayload = source?.progress ?? null;
            const canonicalCanFinish = Boolean(canonicalProgress?.can_finish)
              || source?.interview?.status === 'completed';
            const canonicalCurrentQuestionId = Number(canonicalProgress?.current_question_id);
            const canonicalCurrentIndex = Number.isFinite(canonicalCurrentQuestionId) && canonicalCurrentQuestionId > 0
              ? canonicalQuestions.findIndex((question) => Number(question.id) === canonicalCurrentQuestionId)
              : -1;

            if (canonicalQuestions.length) {
              setLocalQuestions(canonicalQuestions);
              setQuestions(canonicalQuestions);
            }
            if (canonicalCanFinish && canonicalQuestions.length) {
              setCurrentIdx(canonicalQuestions.length);
              setPhase('done');
              setResumeNotice(successNotice);
              return true;
            }
            if (canonicalCurrentIndex >= 0) {
              setCurrentIdx(canonicalCurrentIndex);
              resumeContinuationPendingRef.current = true;
              setPhase('ai_speaking');
              setResumeNotice(successNotice);
              return true;
            }
            return false;
          };

          const runRecoveredSubmit = async () => {
            if (restoredSubmitAppliedRef.current.has(recoveryEnvelope.client_attempt_id)) return;
            const submitPayload = await pendingAnswerRestoreCoordinator.execute(
              recoveryKey,
              () => executePersistedAnswerSubmission({
                userId: currentUserId,
                interviewId: id,
                jobId: recoveryEnvelope.job_id,
                outsourceId: recoveryEnvelope.outsource_id,
                questionId: recoveryEnvelope.question_id,
                clientAttemptId: recoveryEnvelope.client_attempt_id,
                payload: recoveryEnvelope.payload,
                submit: async () => {
                  const recoverySubmitStartedAt = interviewPerformanceNow();
                  const recoveryContext = {
                    traceId: activeTurnTimingRef.current?.traceId,
                    interviewId: id,
                    questionId: recoveryEnvelope.question_id,
                    clientAttemptId: recoveryEnvelope.client_attempt_id,
                  };
                  try {
                    const replayResponse = await submitAnswerWithAttemptReconciliation(
                      () => interviewService.submitAnswer(id, recoveryEnvelope.payload, recoveryContext),
                      {
                        ...SUBMIT_RECOVERY_OPTIONS,
                        observability: recoveryContext,
                        onReconciliationStarted: () => setSubmitActivity('reconciling'),
                      }
                    );
                    trackInterviewDuration('answer_submit_ms', recoverySubmitStartedAt, {
                      ...recoveryContext,
                      outcome: 'success',
                      resultClass: 'refresh_recovery',
                      httpStatus: Number(replayResponse.status) || null,
                    });
                    return replayResponse.data?.data;
                  } catch (error: any) {
                    trackInterviewDuration('answer_submit_ms', recoverySubmitStartedAt, {
                      ...recoveryContext,
                      outcome: 'error',
                      resultClass: 'refresh_recovery',
                      httpStatus: Number(error?.response?.status) || null,
                    });
                    throw error;
                  }
                },
              })
            );
            if (cancelled || restoredSubmitAppliedRef.current.has(recoveryEnvelope.client_attempt_id)) return;

            restoredSubmitAppliedRef.current.add(recoveryEnvelope.client_attempt_id);
            pendingSubmitRetryRef.current = null;
            setPendingSubmitAttemptId(null);
            setSubmitError('');
            setIsRetryingSubmit(false);
            setSubmitActivity('idle');
            setAnswerMode((submitPayload?.answer_mode || '') as AnswerMode);
            setAnswerNotice(submitPayload?.answer_notice || '');
            addAnswer({
              id: submitPayload?.answer?.id ?? 0,
              question_id: recoveryEnvelope.question_id,
              answer_text: recoveryEnvelope.payload.answer_text,
              time_spent_seconds: recoveryEnvelope.payload.time_spent_seconds,
              score: submitPayload?.answer?.score ?? null,
              ai_feedback: submitPayload?.answer?.ai_feedback ?? '',
              needs_followup: submitPayload?.answer?.needs_followup ?? false,
              video_url: submitPayload?.answer?.video_url ?? recoveryEnvelope.payload.video_url ?? null,
              video_mime_type: submitPayload?.answer?.video_mime_type ?? recoveryEnvelope.payload.video_mime_type ?? null,
              video_size_bytes: submitPayload?.answer?.video_size_bytes ?? recoveryEnvelope.payload.video_size_bytes ?? null,
              video_duration_seconds: submitPayload?.answer?.video_duration_seconds ?? recoveryEnvelope.payload.video_duration_seconds ?? null,
            });

            let canonicalSource: any = submitPayload;
            const replayContainsAuthoritativeSessionState = Array.isArray(submitPayload?.questions)
              && Boolean(submitPayload?.progress)
              && Object.prototype.hasOwnProperty.call(submitPayload || {}, 'next_question');
            if (!replayContainsAuthoritativeSessionState) {
              try {
                const canonicalResponse = await interviewService.get(id, {
                  traceId: activeTurnTimingRef.current?.traceId,
                  interviewId: id,
                  questionId: recoveryEnvelope.question_id,
                  clientAttemptId: recoveryEnvelope.client_attempt_id,
                });
                canonicalSource = canonicalResponse.data?.data || submitPayload;
              } catch (_) {}
            }
            if (cancelled) return;

            if (!applyPostSubmitCanonicalState(canonicalSource, 'Jawaban tertunda berhasil dipulihkan dan sudah tersimpan.')) {
              setSubmitError('Jawaban sudah tersimpan, tetapi posisi interview belum dapat dimuat. Muat ulang halaman untuk melanjutkan.');
              setPhase('loading');
            }
          };

          pendingSubmitRetryRef.current = {
            attemptId: recoveryEnvelope.client_attempt_id,
            retry: runRecoveredSubmit,
          };

          try {
            await runRecoveredSubmit();
          } catch (error) {
            if (cancelled) return;
            setSubmitActivity('idle');
            const recoveryFailure = error as Partial<SubmitRecoveryFailure>;
            const recoveryDecision = resolveSubmitFailureUiDecision(recoveryFailure);
            if (!recoveryDecision.retainAttempt) {
              pendingSubmitRetryRef.current = null;
              setPendingSubmitAttemptId(null);
              try {
                const canonicalResponse = await interviewService.get(id);
                if (cancelled) return;
                if (applyPostSubmitCanonicalState(
                  canonicalResponse.data?.data,
                  'Status jawaban diperbarui dari data interview terbaru.'
                )) return;
              } catch (_) {}
            }
            setSubmitError(recoveryDecision.retainAttempt
              ? 'TEMI sedang memastikan jawaban yang tertunda secara otomatis.'
              : getUserFacingError(
                  recoveryFailure.originalError || error,
                  'Jawaban yang dipulihkan tidak dapat dikirim. Silakan rekam jawaban baru.'
                ).message);
            setPhase(recoveryDecision.phase);
            if (!recoveryDecision.retainAttempt) {
              void technicalInterruptionHandlerRef.current(
                'ANSWER_RECONCILIATION_EXHAUSTED',
                'CANDIDATE_AUDIO_CAPTURED',
              );
            }
          }
        } else {
          if (!cancelled) navigate('/lowongan-kerja');
        }
      } catch (_) {
        if (!sessionInitLoggedRef.current) {
          sessionInitLoggedRef.current = true;
          trackInterviewDuration('session_init_ms', sessionInitStartedAtRef.current, {
            traceId: startupTraceIdRef.current,
            interviewId,
            outcome: 'error',
          });
        }
        if (!cancelled) navigate('/lowongan-kerja');
      }
    };

    void loadInterview();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── TTS speak helper ─────────────────────────────────────────
  const deliverSpeechText = useCallback((
    text: string,
    context: 'personalized_intro' | 'greeting' | 'question' = 'question',
    questionId?: number | null,
    requestTimeoutMs?: number,
    deliveryId?: string | null,
    requestAttempt?: number,
    attemptSignal?: AbortSignal,
  ) => new Promise<void>((resolve, reject) => {
    const safeText = text.trim();
    if (!safeText) {
      reject(new Error('Authoritative speech text is empty.'));
      return;
    }

    stopTtsPlayback();
    rememberTemiUtterance(safeText);
    const requestId = ttsRequestIdRef.current;
    const ttsRequestStartedAt = interviewPerformanceNow();
    const activeTurn = activeTurnTimingRef.current;
    const observabilityContext = {
      traceId: activeTurn?.traceId || startupTraceIdRef.current,
      interviewId,
      questionId: Number(questionId) || activeTurn?.questionId || null,
      clientAttemptId: activeTurn?.clientAttemptId || null,
    };
    let firstAudioLogged = false;
    let providerPlaybackStarted = false;
    let settled = false;
    let activeUtterance: SpeechSynthesisUtterance | null = null;

    const isCurrentRequest = () => requestId === ttsRequestIdRef.current;
    const detachAttemptAbort = () => {
      attemptSignal?.removeEventListener('abort', handleAttemptAbort);
    };
    const cleanupBrowserSpeech = (cancelActive: boolean) => {
      if (!activeUtterance) return;
      activeUtterance.onstart = null;
      activeUtterance.onend = null;
      activeUtterance.onerror = null;
      activeUtterance = null;
      if (cancelActive) {
        try { window.speechSynthesis?.cancel(); } catch (_) {}
      }
    };
    const settleSuccess = () => {
      if (settled || !isCurrentRequest()) return;
      settled = true;
      detachAttemptAbort();
      ttsPendingRejectRef.current = null;
      cleanupBrowserSpeech(false);
      cleanupElevenLabsAudio();
      setAiSpeaking(false);
      resolve();
    };
    const settleFailure = (error: Error) => {
      if (settled) return;
      settled = true;
      detachAttemptAbort();
      if (ttsPendingRejectRef.current === settleFailure) ttsPendingRejectRef.current = null;
      cleanupBrowserSpeech(true);
      cleanupElevenLabsAudio();
      setAiSpeaking(false);
      reject(error);
    };
    function handleAttemptAbort() {
      const timeoutError = new Error('Speech delivery attempt cancelled at its deadline.');
      timeoutError.name = 'SpeechDeliveryAttemptCancelledError';
      settleFailure(timeoutError);
    }

    if (attemptSignal?.aborted) {
      handleAttemptAbort();
      return;
    }
    ttsPendingRejectRef.current = settleFailure;
    attemptSignal?.addEventListener('abort', handleAttemptAbort, { once: true });

    const trackFirstAudio = (source: string, provider?: string | null) => {
      if (firstAudioLogged) return;
      firstAudioLogged = true;
      trackInterviewDuration('tts_time_to_first_audio_ms', ttsRequestStartedAt, {
        ...observabilityContext,
        context,
        provider: provider || null,
        source,
        outcome: 'playing',
      });
      if (deliveryId) {
        trackInterviewMetric('question_delivery_first_audio', {
          ...observabilityContext,
          recoveryId: deliveryId,
          requestAttempt,
          source,
          outcome: 'playing',
        });
      }

      if (!startupFirstAudioLoggedRef.current) {
        startupFirstAudioLoggedRef.current = true;
        trackInterviewDuration('interview_startup_total_ms', startupStartedAtRef.current, {
          traceId: startupTraceIdRef.current,
          interviewId,
          questionId: Number(questionId) || null,
          context,
          source,
          outcome: 'first_audio',
        });
      }

      const currentTurn = activeTurnTimingRef.current;
      if (currentTurn && currentTurn.traceId === activeTurn?.traceId && !currentTurn.firstAudioLogged) {
        currentTurn.firstAudioLogged = true;
        trackInterviewDuration('turn_total_ms', currentTurn.startedAt, {
          traceId: currentTurn.traceId,
          interviewId,
          questionId: Number(questionId) || currentTurn.questionId,
          clientAttemptId: currentTurn.clientAttemptId,
          source,
          outcome: 'first_audio',
        });
        if (currentTurn.vadSpeechEndDecisionAt !== null && currentTurn.vadSpeechEndDecisionAt !== undefined) {
          trackInterviewDuration('turn_vad_decision_to_next_audio_ms', currentTurn.vadSpeechEndDecisionAt, {
            traceId: currentTurn.traceId,
            interviewId,
            questionId: Number(questionId) || currentTurn.questionId,
            clientAttemptId: currentTurn.clientAttemptId,
            source,
            outcome: 'first_audio',
          });
        }
      }
    };

    const speakWithBrowserFallback = () => {
      if (settled || !isCurrentRequest()) return;
      if (!ttsSupported) {
        settleFailure(new Error('Browser speech synthesis is unavailable.'));
        return;
      }

      try {
        window.speechSynthesis.cancel();
        const selectedVoice = pickTemiVoice(window.speechSynthesis.getVoices());
        const utterance = new SpeechSynthesisUtterance(safeText);
        activeUtterance = utterance;
        utterance.lang = selectedVoice?.lang || 'id-ID';
        if (selectedVoice) utterance.voice = selectedVoice;
        utterance.rate = 0.9;
        utterance.onstart = () => {
          if (isCurrentRequest()) {
            setAiSpeaking(true);
            trackFirstAudio('browser_fallback', 'browser_speech_synthesis');
          }
        };
        utterance.onend = settleSuccess;
        utterance.onerror = () => settleFailure(new Error('Browser speech synthesis failed.'));
        window.speechSynthesis.speak(utterance);
      } catch (_) {
        settleFailure(new Error('Browser speech synthesis failed.'));
      }
    };

    if (!interviewId) {
      speakWithBrowserFallback();
      return;
    }

    setAiSpeaking(true);
    interviewService
      .synthesizeSpeech(interviewId, safeText, context, observabilityContext, requestTimeoutMs)
      .then((res) => {
        if (settled || !isCurrentRequest()) return;
        const provider = String(res.headers?.['x-tts-provider'] || '').trim() || null;
        trackInterviewDuration('tts_request_ms', ttsRequestStartedAt, {
          ...observabilityContext,
          context,
          provider,
          outcome: 'success',
          httpStatus: Number(res.status) || null,
        });
        const audioBlob = res.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: 'audio/mpeg' });

        if (!audioBlob.size) {
          speakWithBrowserFallback();
          return;
        }

        const objectUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(objectUrl);
        ttsObjectUrlRef.current = objectUrl;
        ttsAudioRef.current = audio;
        audio.onplay = () => {
          if (isCurrentRequest()) {
            providerPlaybackStarted = true;
            setAiSpeaking(true);
            trackFirstAudio('provider_audio', provider);
          }
        };
        audio.onended = settleSuccess;
        audio.onerror = () => {
          if (!isCurrentRequest()) return;
          cleanupElevenLabsAudio();
          if (providerPlaybackStarted) {
            settleFailure(new Error('Provider playback ended before the segment completed.'));
          } else {
            speakWithBrowserFallback();
          }
        };
        audio.play().catch(() => {
          if (!isCurrentRequest()) return;
          cleanupElevenLabsAudio();
          speakWithBrowserFallback();
        });
      })
      .catch((error: any) => {
        if (settled) return;
        trackInterviewDuration('tts_request_ms', ttsRequestStartedAt, {
          ...observabilityContext,
          context,
          outcome: 'error',
          httpStatus: Number(error?.response?.status) || null,
        });
        speakWithBrowserFallback();
      });
  }), [cleanupElevenLabsAudio, interviewId, rememberTemiUtterance, stopTtsPlayback]);

  const deliverPromptWithRecovery = useCallback(({
    deliveryId,
    canonicalQuestionIdentity,
    segments,
    context,
    questionId,
  }: {
    deliveryId: string;
    canonicalQuestionIdentity: string | null;
    segments: string[];
    context: 'personalized_intro' | 'greeting' | 'question';
    questionId?: number | null;
  }) => {
    if (canonicalQuestionIdentity) {
      authoritativeQuestionIdentityRef.current = canonicalQuestionIdentity;
      deliveredQuestionIdentityRef.current = null;
      deliveredQuestionTimingRef.current = null;
    }
    const observabilityContext = {
      traceId: activeTurnTimingRef.current?.traceId || startupTraceIdRef.current,
      interviewId,
      questionId: Number(questionId) || null,
      clientAttemptId: activeTurnTimingRef.current?.clientAttemptId || null,
    };

    return questionDeliveryCoordinatorRef.current.execute({
      deliveryId,
      observability: observabilityContext,
      onStateChange: (snapshot) => {
        setQuestionDelivery(snapshot);
        if (snapshot.state === 'recovery') setPhase('delivery_recovery');
      },
      operation: async (attempt, requestTimeoutMs, remainingRequestTimeoutMs, attemptSignal) => {
        const safeSegments = segments.map((segment) => segment.trim()).filter(Boolean);
        if (!safeSegments.length) throw new Error('Authoritative prompt has no playable segment.');
        for (const segment of safeSegments) {
          await deliverSpeechText(
            segment,
            context,
            questionId,
            Math.min(requestTimeoutMs, remainingRequestTimeoutMs()),
            deliveryId,
            attempt,
            attemptSignal,
          );
        }
      },
    }).then(() => {
      if (canonicalQuestionIdentity) {
        deliveredQuestionIdentityRef.current = canonicalQuestionIdentity;
        deliveredQuestionTimingRef.current = {
          identity: canonicalQuestionIdentity,
          completedAt: interviewPerformanceNow(),
        };
      }
    });
  }, [deliverSpeechText, interviewId]);

  const speakTemiTranscriptTurn = useCallback((
    text: string,
    onDone?: () => void,
    options: {
      questionId?: number | null;
      context?: 'personalized_intro' | 'greeting' | 'question';
      logTurn?: boolean;
      finalStatus?: ConversationTurnStatus;
    } = {}
  ) => {
    const safeText = String(text || '').trim();
    if (!safeText) {
      onDone?.();
      return;
    }

    const shouldLogTurn = options.logTurn !== false;
    const turnId = shouldLogTurn
      ? appendConversationTurn({
          speaker: 'temi',
          text: '',
          status: 'speaking',
          questionId: options.questionId ?? null,
        })
      : null;

    const question = questions.find((item) => Number(item.id) === Number(options.questionId)) || questions[currentIdx];
    const canonicalQuestionIdentity = question
      ? buildAuthoritativeQuestionDeliveryId({
          interviewId,
          questionId: Number(question.id),
          questionType: question.question_type,
          questionIndex: question.question_index,
        })
      : null;
    transientDeliverySequenceRef.current += 1;
    const deliveryId = `${canonicalQuestionIdentity || `iv-${interviewId}:opening`}:prompt-${transientDeliverySequenceRef.current}`;
    void deliverPromptWithRecovery({
      deliveryId,
      canonicalQuestionIdentity,
      segments: [safeText],
      context: options.context || 'question',
      questionId: options.questionId ?? null,
    }).then(() => {
      if (turnId) {
        updateConversationTurn(turnId, { text: safeText, status: options.finalStatus });
      }
      onDone?.();
    }).catch((error) => {
      if (isInterviewQuestionDeliveryCancelled(error)) return;
      void technicalInterruptionHandlerRef.current(
        'QUESTION_DELIVERY_EXHAUSTED',
        canonicalQuestionIdentity ? 'QUESTION_AUTHORITATIVE' : 'QUESTION_AUTHORITATIVE',
      );
    });
  }, [appendConversationTurn, currentIdx, deliverPromptWithRecovery, interviewId, questions, updateConversationTurn]);

  const speakQuestion = useCallback(
    (question: InterviewQuestion, questionIndex: number) => {
      const safeQuestionText = String(question?.question_text || '').trim();
      const shouldUseResumeContinuation = resumeContinuationPendingRef.current;
      resumeContinuationPendingRef.current = false;
      const spokenSegments = buildTemiQuestionSegments(safeQuestionText, questionIndex, {
        resumeContinuation: shouldUseResumeContinuation,
        questionType: question.question_type,
      });

      const questionKey = String(question?.id || `idx-${questionIndex}`);
      const canonicalQuestionIdentity = buildAuthoritativeQuestionDeliveryId({
        interviewId,
        questionId: Number(question.id),
        questionType: question.question_type,
        questionIndex: question.question_index,
      });
      const shouldLogQuestion = !askedQuestionIdsRef.current.has(questionKey);
      if (shouldLogQuestion) {
        askedQuestionIdsRef.current.add(questionKey);
      }

      const questionTurnId = shouldLogQuestion
        ? appendConversationTurn({
            speaker: 'temi',
            text: '',
            status: 'speaking',
            questionId: Number(question.id) || null,
          })
        : null;
      void deliverPromptWithRecovery({
        deliveryId: canonicalQuestionIdentity,
        canonicalQuestionIdentity,
        segments: safeQuestionText ? spokenSegments : [],
        context: 'question',
        questionId: Number(question.id) || null,
      }).then(() => {
        if (questionTurnId) {
          updateConversationTurn(questionTurnId, { text: safeQuestionText, status: undefined });
        }
        setPhase('idle');
      }).catch((error) => {
        if (isInterviewQuestionDeliveryCancelled(error)) return;
        void technicalInterruptionHandlerRef.current(
          'QUESTION_DELIVERY_EXHAUSTED',
          'QUESTION_AUTHORITATIVE',
        );
      });
    },
    [appendConversationTurn, deliverPromptWithRecovery, interviewId, updateConversationTurn]
  );

  const handleNoAnswerPrompt = useCallback((questionId?: number | null) => {
    const targetQuestion = questions.find((item) => Number(item.id) === Number(questionId)) || questions[currentIdx];
    const targetQuestionId = Number(targetQuestion?.id || questionId) || null;
    const promptKey = String(targetQuestionId || `idx-${currentIdx}`);
    const nextAttempt = (noAnswerPromptCountsRef.current[promptKey] || 0) + 1;
    noAnswerPromptCountsRef.current[promptKey] = nextAttempt;
    const shouldEndInterview = nextAttempt >= MAX_NO_ANSWER_PROMPTS;
    const promptText = buildNoAnswerPromptText(nextAttempt);

    clearNoSpeechPromptTimer();
    setTranscribeError(false);
    setSubmitError('');
    setIsUserSpeaking(false);
    setIsTranscribing(false);
    setPhase('prompting');

    const promptTurnId = appendConversationTurn({
      speaker: 'temi',
      text: '',
      status: 'speaking',
      questionId: targetQuestionId,
    });

    const canonicalQuestionIdentity = targetQuestion
      ? buildAuthoritativeQuestionDeliveryId({
          interviewId,
          questionId: Number(targetQuestion.id),
          questionType: targetQuestion.question_type,
          questionIndex: targetQuestion.question_index,
        })
      : null;
    const deliveryId = `${canonicalQuestionIdentity || `iv-${interviewId}:question-${targetQuestionId || 0}`}:retry-${nextAttempt}`;
    void deliverPromptWithRecovery({
      deliveryId,
      canonicalQuestionIdentity,
      segments: [promptText],
      context: 'question',
      questionId: targetQuestionId,
    }).then(() => {
      updateConversationTurn(promptTurnId, {
        text: promptText,
        status: shouldEndInterview ? 'saved' : undefined,
      });
      setAiSpeaking(false);
      setPhase(shouldEndInterview ? 'finishing' : 'idle');
    }).catch((error) => {
      if (isInterviewQuestionDeliveryCancelled(error)) return;
      void technicalInterruptionHandlerRef.current(
        'QUESTION_DELIVERY_EXHAUSTED',
        'QUESTION_AUTHORITATIVE',
      );
    });
  }, [
    appendConversationTurn,
    clearNoSpeechPromptTimer,
    currentIdx,
    questions,
    deliverPromptWithRecovery,
    interviewId,
    updateConversationTurn,
  ]);

  useEffect(() => {
    if (phase !== 'greeting' || mediaAvailability !== 'ready') return;
    if (!personalizedIntroPlaybackGuardRef.current.tryStart()) return;

    const personalizedIntro = personalizedIntroTextRef.current.trim();
    if (!personalizedIntro) {
      setPhase('ai_speaking');
      return;
    }

    setAiSpeaking(true);
    speakTemiTranscriptTurn(personalizedIntro, () => {
      setAiSpeaking(false);
      setPhase('ai_speaking');
    }, {
      context: 'personalized_intro',
      questionId: null,
      logTurn: true,
    });
  }, [mediaAvailability, phase, speakTemiTranscriptTurn]);

  useEffect(() => {
    if (phase !== 'ai_speaking' || mediaAvailability !== 'ready') return;
    const q = questions[currentIdx];
    if (!q) return;
    startTimeRef.current = Date.now();
    resetRecordedAnswerState();
    speakQuestion(q, currentIdx);
  }, [currentIdx, mediaAvailability, phase, questions, resetRecordedAnswerState, speakQuestion]);

  // ── STT: MediaRecorder + Whisper backend ─────────────────
  const submitAnswerPayload = useCallback(async (submitPayload: InterviewAnswerRecoveryPayload) => {
    const submitStartedAt = interviewPerformanceNow();
    const activeTurn = activeTurnTimingRef.current;
    const observabilityContext = {
      traceId: activeTurn?.traceId || createInterviewTraceId('turn'),
      interviewId,
      questionId: submitPayload.question_id,
      clientAttemptId: submitPayload.client_attempt_id,
    };
    setSubmitActivity('evaluating');
    let res;
    try {
      res = await submitAnswerWithAttemptReconciliation(
        () => interviewService.submitAnswer(interviewId, submitPayload, observabilityContext),
        {
          ...SUBMIT_RECOVERY_OPTIONS,
          observability: observabilityContext,
          onReconciliationStarted: () => setSubmitActivity('reconciling'),
        }
      );
      trackInterviewDuration('answer_submit_ms', submitStartedAt, {
        ...observabilityContext,
        outcome: 'success',
        resultClass: res.data?.data?.idempotent_replay ? 'idempotent_replay' : 'completed',
        httpStatus: Number(res.status) || null,
      });
    } catch (error: any) {
      trackInterviewDuration('answer_submit_ms', submitStartedAt, {
        ...observabilityContext,
        outcome: 'error',
        httpStatus: Number(error?.response?.status) || null,
      });
      throw error;
    }
    const payload = res.data?.data;
    setAnswerMode((payload?.answer_mode || '') as AnswerMode);
    setAnswerNotice(payload?.answer_notice || '');
    addAnswer({
      id: payload?.answer?.id ?? 0,
      question_id: submitPayload.question_id,
      answer_text: submitPayload.answer_text,
      time_spent_seconds: submitPayload.time_spent_seconds,
      score: payload?.answer?.score ?? null,
      ai_feedback: payload?.answer?.ai_feedback ?? '',
      needs_followup: payload?.answer?.needs_followup ?? false,
      video_url: payload?.answer?.video_url ?? submitPayload.video_url ?? null,
      video_mime_type: payload?.answer?.video_mime_type ?? submitPayload.video_mime_type ?? null,
      video_size_bytes: payload?.answer?.video_size_bytes ?? submitPayload.video_size_bytes ?? null,
      video_duration_seconds: payload?.answer?.video_duration_seconds ?? submitPayload.video_duration_seconds ?? null,
    });
    return payload;
  }, [addAnswer, interviewId]);

  const saveRecordedAnswer = useCallback(async ({
    question,
    clientAttemptId,
    answerText,
    rawAnswerText,
    transcriptQuality: answerTranscriptQuality,
    transcriptMode: answerTranscriptMode,
    elapsedSeconds,
  }: RecordedAnswerSaveInput) => {
    const cleanedAnswer = answerText.trim();
    if (!question || !cleanedAnswer) {
      throw new Error('ANSWER_TRANSCRIPT_EMPTY');
    }

    const qualityBand = resolveTranscriptQualityBand(answerTranscriptQuality);
    const requiresTranscriptReview = qualityBand === 'review' || qualityBand === 'critical';
    const submitPayload: InterviewAnswerRecoveryPayload = {
      question_id: question.id,
      answer_text: cleanedAnswer,
      raw_answer: rawAnswerText.trim() || cleanedAnswer,
      transcript_reviewed: true,
      transcript_risk_acknowledged: requiresTranscriptReview,
      transcript_quality_score: answerTranscriptQuality?.score ?? null,
      transcript_review_reason: answerTranscriptQuality?.review_reason ?? null,
      transcript_quality_flags: Array.isArray(answerTranscriptQuality?.flags) ? answerTranscriptQuality.flags : [],
      transcript_risky_shift_detected: Boolean(answerTranscriptQuality?.risky_shift_detected),
      transcript_prefer_raw: Boolean(answerTranscriptQuality?.prefer_raw),
      transcript_mode: answerTranscriptMode || null,
      client_attempt_id: clientAttemptId,
      video_url: null,
      video_mime_type: null,
      video_size_bytes: null,
      video_duration_seconds: null,
      time_spent_seconds: elapsedSeconds,
      auto_submit: false,
      conversation_mode: false,
      seamless_mode: false,
    };

    const currentUserId = readAuthUserId();
    if (!currentUserId) {
      const error = new Error('PENDING_ANSWER_RECOVERY_USER_UNAVAILABLE');
      error.name = 'PendingAnswerRecoveryStorageError';
      throw error;
    }

    return executePersistedAnswerSubmission({
      userId: currentUserId,
      interviewId,
      jobId: jobIdNum,
      outsourceId: outsourceIdVal,
      questionId: question.id,
      clientAttemptId,
      payload: submitPayload,
      submit: () => submitAnswerPayload(submitPayload),
    });
  }, [interviewId, jobIdNum, outsourceIdVal, submitAnswerPayload]);

  const finishCurrentRecording = useCallback(() => {
    const recorder = recRef.current as MediaRecorder | null;
    debugInterviewSession('finishCurrentRecording called', {
      hasRecorder: Boolean(recorder),
      recorderState: recorder?.state,
      phase: phaseRef.current,
    });
    if (!recorder || recorder.state !== 'recording') return;

    clearNoSpeechPromptTimer();
    setSubmitError('');
    setIsUserSpeaking(false);
    setIsTranscribing(true);
    setPhase('submitting');
    beginBackgroundSave();
    backgroundSaveRecordersRef.current.add(recorder);
    markActiveVadTermination('deliberate_termination');

    try {
      try {
        recorder.requestData?.();
        debugInterviewSession('audio recorder requestData called');
      } catch (_) {}
      debugInterviewSession('audio recorder stop requested');
      markActiveRecorderStopRequest(recorder);
      recorder.stop();
      debugInterviewSession('audio recorder stop returned', {
        recorderState: recorder.state,
        backgroundSaveCount: backgroundSaveCountRef.current,
      });
      if (audioStopTimeoutRef.current !== null) {
        window.clearTimeout(audioStopTimeoutRef.current);
      }
      audioStopTimeoutRef.current = window.setTimeout(() => {
        if (backgroundSaveRecordersRef.current.has(recorder)) {
          debugInterviewSession('audio recorder stop watchdog fired', {
            recorderState: recorder.state,
          });
          audioStopFallbackRef.current?.();
        }
      }, AUDIO_RECORDER_STOP_TIMEOUT_MS);
    } catch (_) {
      debugInterviewSession('audio recorder stop failed');
      if (audioStopTimeoutRef.current !== null) {
        window.clearTimeout(audioStopTimeoutRef.current);
        audioStopTimeoutRef.current = null;
      }
      if (backgroundSaveRecordersRef.current.delete(recorder)) {
        finishBackgroundSave();
      }
      setIsTranscribing(false);
      setPhase('idle');
    }
  }, [
    beginBackgroundSave,
    clearNoSpeechPromptTimer,
    finishBackgroundSave,
    markActiveRecorderStopRequest,
    markActiveVadTermination,
  ]);

  const startRecording = useCallback(() => {
    if (recorderStartLockRef.current || recRef.current?.state === 'recording') {
      return;
    }
    const activePhase = phaseRef.current;
    if (activePhase !== 'idle') {
      return;
    }
    const recordingQuestion = questions[currentIdx];
    if (!recordingQuestion) {
      setSubmitError('Pertanyaan belum siap. Tunggu sebentar lalu coba lagi.');
      return;
    }
    const recordingQuestionIndex = currentIdx;
    const recordingQuestionId = Number(recordingQuestion.id) || null;
    const expectedDeliveryIdentity = buildAuthoritativeQuestionDeliveryId({
      interviewId,
      questionId: Number(recordingQuestion.id),
      questionType: recordingQuestion.question_type,
      questionIndex: recordingQuestion.question_index,
    });
    if (
      authoritativeQuestionIdentityRef.current !== expectedDeliveryIdentity
      || deliveredQuestionIdentityRef.current !== expectedDeliveryIdentity
    ) {
      return;
    }
    const recordingQuestionText = recordingQuestion.question_text || '';
    const recordingStartedAt = startTimeRef.current || Date.now();
    discardRecordingOnStopRef.current = false;
    pendingVadSpeechEndDecisionRef.current = null;
    noSpeechPromptOnStopRef.current = null;
    clearNoSpeechPromptTimer();

    if (mediaAvailability !== 'ready' || !streamRef.current) {
      setSubmitError('Kamera dan mikrofon harus aktif sebelum mulai menjawab.');
      return;
    }
    recorderStartLockRef.current = true;
    stopTtsPlayback();
    setIsUserSpeaking(false);

    if (!mediaRecorderSupported || !streamRef.current) {
      recorderStartLockRef.current = false;
      void technicalInterruptionHandlerRef.current(
        'MEDIA_ACCESS_EXHAUSTED',
        'QUESTION_DELIVERED',
      );
      return;
    }

    // Use audio-only stream to avoid MediaRecorder issues with combined video+audio streams
    const audioTracks = streamRef.current.getAudioTracks();
    if (audioTracks.length === 0) {
      recorderStartLockRef.current = false;
      handleMediaFailure('lost', 'Mikrofon tidak lagi tersedia. TEMI akan mencoba menyambungkan ulang otomatis.');
      return;
    }
    const audioOnlyStream = new MediaStream(audioTracks);
    const deliveryTiming = deliveredQuestionTimingRef.current?.identity === expectedDeliveryIdentity
      ? deliveredQuestionTimingRef.current
      : null;
    const measurementSequence = vadMeasurementSequenceRef.current + 1;
    vadMeasurementSequenceRef.current = measurementSequence;
    const vadMeasurement: ActiveVadMeasurement = {
      identity: `interview-${interviewId}:question-${recordingQuestionId || 0}:recording-${measurementSequence}`,
      traceId: createInterviewTraceId('turn'),
      questionId: recordingQuestionId,
      deliveryCompletedAt: deliveryTiming?.completedAt ?? null,
      recorderStartedAt: null,
      firstSpeechAt: null,
      lastSpeechAt: null,
      vadDecisionAt: null,
      stopRequestedAt: null,
      blobReadyAt: null,
      sttStartedAt: null,
      terminationReason: null,
      summarySnapshot: null,
      summaryEmitted: false,
      emittedMetrics: new Set<string>(),
      recorder: null,
    };
    activeVadMeasurementRef.current = vadMeasurement;
    startSilenceMonitor(audioOnlyStream, {
      autoStop: AUTO_CONVERSATION_ENABLED,
      onSpeechStart: () => {
        clearNoSpeechPromptTimer();
        setIsUserSpeaking(true);
      },
      onSpeechPause: () => setIsUserSpeaking(false),
      onSpeechEnd: (_reason, decisionAt) => {
        const traceId = vadMeasurement.traceId;
        pendingVadSpeechEndDecisionRef.current = {
          traceId,
          questionId: recordingQuestionId,
          startedAt: decisionAt,
        };
        trackInterviewMetric('turn_vad_speech_end_decision', {
          traceId,
          interviewId,
          questionId: recordingQuestionId,
          stage: 'vad_speech_end_decision',
          outcome: 'stop_requested',
        });
        finishCurrentRecording();
      },
    });

    // Find a supported mimeType — try in order, fall back to no mimeType (browser default)
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4',
    ];
    const mimeCandidates = candidates.filter((m) => MediaRecorder.isTypeSupported(m));
    const videoMimeCandidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm',
      'video/mp4',
    ].filter((m) => MediaRecorder.isTypeSupported(m));

    const tryStartRecorder = (mimeType: string | '', videoMimeType: string | '') => {
      const audioChunks: BlobPart[] = [];
      const videoChunks: BlobPart[] = [];
      const audioRecorderOptions: MediaRecorderOptions = {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: AUDIO_RECORDING_BITS_PER_SECOND,
      };
      const videoRecorderOptions: MediaRecorderOptions = {
        ...(videoMimeType ? { mimeType: videoMimeType } : {}),
        videoBitsPerSecond: VIDEO_RECORDING_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_RECORDING_BITS_PER_SECOND,
      };
      const recorder = new MediaRecorder(audioOnlyStream, audioRecorderOptions);
      const videoRecorder = new MediaRecorder(streamRef.current as MediaStream, videoRecorderOptions);
      vadMeasurement.recorder = recorder;

      const actualMime = recorder.mimeType || mimeType || 'audio/webm';
      const actualVideoMime = videoRecorder.mimeType || videoMimeType || 'video/webm';
      let videoStopSettled = false;
      let videoStopTimeoutId: number | null = null;
      let resolveVideoStop: ((blob: Blob | null) => void) | null = null;
      const buildVideoBlob = () => new Blob(videoChunks, { type: actualVideoMime });
      const videoStopPromise = new Promise<Blob | null>((resolve) => {
        resolveVideoStop = (blob: Blob | null) => {
          if (videoStopSettled) return;
          videoStopSettled = true;
          if (videoStopTimeoutId !== null) {
            window.clearTimeout(videoStopTimeoutId);
            videoStopTimeoutId = null;
          }
          resolve(blob);
        };
        videoRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) videoChunks.push(event.data);
        };
        videoRecorder.onerror = () => resolveVideoStop?.(videoChunks.length ? buildVideoBlob() : null);
        videoRecorder.onstop = () => resolveVideoStop?.(buildVideoBlob());
      });
      const stopVideoRecorderSafely = () => {
        if (!videoStopSettled) {
          try {
            if (videoRecorder.state === 'recording') {
              videoRecorder.stop();
            } else {
              resolveVideoStop?.(videoChunks.length ? buildVideoBlob() : null);
            }
          } catch (_) {
            resolveVideoStop?.(videoChunks.length ? buildVideoBlob() : null);
          }
        }

        if (!videoStopSettled && videoStopTimeoutId === null) {
          videoStopTimeoutId = window.setTimeout(() => {
            resolveVideoStop?.(videoChunks.length ? buildVideoBlob() : null);
          }, VIDEO_RECORDER_STOP_TIMEOUT_MS);
        }

        return videoStopPromise;
      };

      recorder.ondataavailable = (e) => {
        debugInterviewSession('audio recorder dataavailable', { size: e.data.size });
        if (e.data.size > 0) audioChunks.push(e.data);
      };
      let audioStopSettled = false;
      const releaseRecordingControls = () => {
        clearNoSpeechPromptTimer();
        stopSilenceMonitor();
        setIsUserSpeaking(false);
        recorderStartLockRef.current = false;
        if (audioStopTimeoutRef.current !== null) {
          window.clearTimeout(audioStopTimeoutRef.current);
          audioStopTimeoutRef.current = null;
        }
        if (audioStopFallbackRef.current === completeStoppedRecording) {
          audioStopFallbackRef.current = null;
        }
        if (recRef.current === recorder) recRef.current = null;
        if (videoRecRef.current === videoRecorder) videoRecRef.current = null;
        if (activeVadMeasurementRef.current?.identity === vadMeasurement.identity) {
          activeVadMeasurementRef.current = null;
        }
      };

      const completeStoppedRecording = async () => {
        debugInterviewSession('completeStoppedRecording called', {
          audioStopSettled,
          audioChunks: audioChunks.length,
          audioSizes: audioChunks.map((chunk: any) => Number(chunk?.size) || 0),
        });
        if (audioStopSettled) return;
        audioStopSettled = true;
        if (!vadMeasurement.terminationReason) {
          markActiveVadTermination('deliberate_termination');
        }
        const vadSpeechEndDecision = pendingVadSpeechEndDecisionRef.current;
        pendingVadSpeechEndDecisionRef.current = null;
        const turnTiming: ActiveTurnTiming = {
          traceId: vadSpeechEndDecision?.traceId || vadMeasurement.traceId,
          clientAttemptId: null,
          questionId: recordingQuestionId,
          startedAt: interviewPerformanceNow(),
          firstAudioLogged: false,
          listeningLogged: false,
          vadSpeechEndDecisionAt: vadSpeechEndDecision?.startedAt ?? null,
        };
        activeTurnTimingRef.current = turnTiming;
        const blob = new Blob(audioChunks, { type: actualMime });
        markVadBlobReady(vadMeasurement, interviewPerformanceNow());
        debugInterviewSession('audio blob built', {
          size: blob.size,
          type: blob.type,
          chunks: audioChunks.length,
        });
        if (discardRecordingOnStopRef.current) {
          debugInterviewSession('recording discarded on stop');
          const pendingNoSpeechPrompt = noSpeechPromptOnStopRef.current;
          discardRecordingOnStopRef.current = false;
          noSpeechPromptOnStopRef.current = null;
          await stopVideoRecorderSafely().catch(() => null);
          releaseRecordingControls();
          setIsTranscribing(false);
          setIsUserSpeaking(false);
          if (pendingNoSpeechPrompt) {
            handleNoAnswerPrompt(pendingNoSpeechPrompt.questionId);
          } else {
            setPhase('idle');
          }
          if (backgroundSaveRecordersRef.current.delete(recorder)) {
            finishBackgroundSave();
          }
          return;
        }

        setTranscribeError(false);
        let candidateTurnId: string | null = null;
        try {
          const likelySilentRecording = isLikelySilentRecording(blob.size);
          if (likelySilentRecording) {
            debugInterviewSession('audio considered silent', { size: blob.size });
            await stopVideoRecorderSafely();
            releaseRecordingControls();
            setTranscribeError(false);
            setSubmitError('');
            setIsTranscribing(false);
            handleNoAnswerPrompt(recordingQuestionId);
            return;
          }

          const uploadClientAttemptId = buildClientAttemptId(recordingQuestionId);
          turnTiming.clientAttemptId = uploadClientAttemptId;
          const uploadRecordedAt = new Date().toISOString();
          const uploadDurationSeconds = Math.max(1, Math.round((Date.now() - recordingStartedAt) / 1000));
          const nextIdx = recordingQuestionIndex + 1;

          candidateTurnId = appendConversationTurn({
            speaker: 'candidate',
            text: '',
            status: 'processing',
            questionId: recordingQuestionId,
          });
          setIsTranscribing(true);
          debugInterviewSession('starting transcribe request', {
            audioSize: blob.size,
            questionId: recordingQuestionId,
          });
          const questionTextForTranscription = recordingQuestion.question_type === 'candidate_question'
            ? ''
            : recordingQuestionText;
          const sttStartedAt = interviewPerformanceNow();
          const sttObservability = {
            traceId: turnTiming.traceId,
            interviewId,
            questionId: recordingQuestionId,
            clientAttemptId: uploadClientAttemptId,
          };
          const transcribeTask = sttRecoveryCoordinatorRef.current.execute({
            recoveryId: uploadClientAttemptId,
            blob,
            observability: sttObservability,
            onStateChange: (snapshot) => {
              setSttRecoveryPhase(snapshot.phase);
              if (snapshot.phase === 'transcribing' || snapshot.phase === 'fast_retry_wait' || snapshot.phase === 'connectivity_wait') {
                setSttTerminalFailure(false);
                setTranscribeError(false);
                setSubmitError('');
              }
            },
            operation: async (retainedBlob, _requestAttempt, requestTimeoutMs) => {
              markVadSttStart(vadMeasurement, interviewPerformanceNow());
              const response = await interviewService.transcribeAudio(
                retainedBlob,
                jobIdNum,
                questionTextForTranscription,
                interviewId,
                {
                  conversationMode: true,
                  transcriptPipeline: 'conversation',
                  timeoutMs: requestTimeoutMs,
                  observability: sttObservability,
                }
              );
              const responseOutcome = resolveTranscriptionPayloadOutcome(response.data);
              if (responseOutcome.recoveryClass === 'infrastructure' || responseOutcome.recoveryClass === 'rate_limit') {
                const responseError: any = new Error(responseOutcome.message);
                responseError.response = response;
                throw responseError;
              }
              return response;
            },
          });
          const videoBlob = await stopVideoRecorderSafely();
          releaseRecordingControls();
          const pendingUpload: PendingVideoUpload = videoBlob && videoBlob.size > 0 && recordingQuestionId
            ? {
                blob: videoBlob,
                clientAttemptId: uploadClientAttemptId,
                recordedAt: uploadRecordedAt,
                durationSeconds: uploadDurationSeconds,
                questionId: recordingQuestionId,
              }
            : null;
          debugInterviewSession('video recorder stopped', {
            videoSize: videoBlob?.size || 0,
            hasPendingUpload: Boolean(pendingUpload),
          });

          const transcribeResult = await Promise.resolve(transcribeTask)
            .then((value) => ({ status: 'fulfilled' as const, value }))
            .catch((reason) => ({ status: 'rejected' as const, reason }));
          trackInterviewDuration('stt_total_ms', sttStartedAt, {
            traceId: turnTiming.traceId,
            interviewId,
            questionId: recordingQuestionId,
            clientAttemptId: uploadClientAttemptId,
            outcome: transcribeResult.status === 'fulfilled' ? 'success' : 'error',
            httpStatus: transcribeResult.status === 'fulfilled'
              ? Number(transcribeResult.value?.status) || null
              : Number(transcribeResult.reason?.response?.status) || null,
          });
          debugInterviewSession('transcribe settled', {
            transcribeStatus: transcribeResult.status,
          });
          setIsTranscribing(false);

          if (transcribeResult.status === 'rejected') {
            if (isInterviewSttRecoveryCancelled(transcribeResult.reason)) {
              return;
            }
            debugInterviewSession('transcribe rejected', transcribeResult.reason);
            if (
              transcribeResult.reason instanceof InterviewSttRecoveryFailure
              && transcribeResult.reason.exhausted
            ) {
              if (candidateTurnId) {
                updateConversationTurn(candidateTurnId, {
                  text: 'Jawaban belum dapat diproses karena kendala koneksi.',
                  status: 'error',
                });
              }
              void technicalInterruptionHandlerRef.current(
                'STT_INFRASTRUCTURE_EXHAUSTED',
                'CANDIDATE_AUDIO_CAPTURED',
              );
              return;
            }
            const transcribeOutcome = transcribeResult.reason instanceof InterviewSttRecoveryFailure
              ? transcribeResult.reason.outcome
              : resolveTranscriptionErrorOutcome(transcribeResult.reason);
            const isAudioFailure = transcribeOutcome.recoveryClass === 'audio';
            setSttRecoveryPhase('failed');
            if (isAudioFailure) {
              setSttTerminalFailure(false);
              setTranscribeError(false);
              setSubmitError('');
              if (candidateTurnId) removeConversationTurn(candidateTurnId);
              handleNoAnswerPrompt(recordingQuestionId);
            } else {
              setSttTerminalFailure(true);
              void technicalInterruptionHandlerRef.current(
                'STT_INFRASTRUCTURE_EXHAUSTED',
                'CANDIDATE_AUDIO_CAPTURED',
              );
            }
            return;
          }

          const transcribeRes = transcribeResult.value;
          setSttRecoveryPhase('idle');
          setSttTerminalFailure(false);
          const transcribeOutcome = resolveTranscriptionPayloadOutcome(transcribeRes.data);
          if (transcribeOutcome.kind !== 'success') {
            if (shouldConsumeNoAnswerAttempt(transcribeOutcome)) {
              setTranscribeError(false);
              setSubmitError('');
              if (candidateTurnId) removeConversationTurn(candidateTurnId);
              handleNoAnswerPrompt(recordingQuestionId);
              return;
            }

            setTranscribeError(false);
            setSubmitError('');
            if (candidateTurnId) removeConversationTurn(candidateTurnId);
            handleNoAnswerPrompt(recordingQuestionId);
            return;
          }
          const text: string = transcribeRes.data?.transcript || '';
          const rawText: string = transcribeRes.data?.raw_transcript || text;
          const trimmedText = text.trim();
          const trimmedRawText = rawText.trim();
          const displayedAnswerText = trimmedRawText || trimmedText;
          const nextTranscriptQuality = transcribeRes.data?.transcript_quality || null;

          if (
            !displayedAnswerText ||
            !isMeaningfulAnswerTranscript(displayedAnswerText) ||
            isLikelyTemiPromptEcho(displayedAnswerText, [
              recordingQuestionText,
              ...recentTemiUtterancesRef.current,
            ])
          ) {
            setTranscribeError(false);
            setSubmitError('');
            if (candidateTurnId) {
              removeConversationTurn(candidateTurnId);
            }
            handleNoAnswerPrompt(recordingQuestionId);
            return;
          }

          if (candidateTurnId) {
            updateConversationTurn(candidateTurnId, {
              speaker: 'candidate',
              text: displayedAnswerText,
              status: 'processing',
            });
          }
          if (recordingQuestionId) {
            noAnswerPromptCountsRef.current[String(recordingQuestionId)] = 0;
          }

          const interruptionResolution = buildInterviewInterruptionResolution({
            answerText: displayedAnswerText,
            question: recordingQuestion,
            jobDetail: jobDetailRef.current,
          });
          if (interruptionResolution.intent !== 'answer') {
            if (candidateTurnId) {
              updateConversationTurn(candidateTurnId, { status: undefined });
            }

            const finishAfterInterruption = () => {
              setCurrentIdx(questions.length);
              setPhase('closing');
              speakTemiTranscriptTurn(
                interruptionResolution.responseText || buildTemiClosingUtterance(),
                () => setPhase('finishing'),
                { questionId: recordingQuestionId }
              );
            };

            if (interruptionResolution.endSession) {
              finishAfterInterruption();
              return;
            }

            const returnToCurrentQuestion = () => {
              startTimeRef.current = Date.now();
              setPhase('idle');
            };

            setPhase('responding');
            speakTemiTranscriptTurn(interruptionResolution.responseText || 'Baik, kita lanjutkan dari pertanyaan yang sama.', () => {
              if (interruptionResolution.repeatQuestion && recordingQuestionText) {
                speakTemiTranscriptTurn(recordingQuestionText, returnToCurrentQuestion, {
                  questionId: recordingQuestionId,
                });
                return;
              }

              returnToCurrentQuestion();
            }, {
              questionId: recordingQuestionId,
            });
            return;
          }

          const isCandidateQuestion = recordingQuestion.question_type === 'candidate_question';
          const candidateQuestionKey = String(recordingQuestionId || 'candidate_question');
          const candidateDoneAsking = isCandidateQuestion && isCandidateDoneAsking(displayedAnswerText);
          let candidateQuestionTexts = candidateQuestionTextsRef.current[candidateQuestionKey] || [];
          if (isCandidateQuestion && !candidateDoneAsking) {
            candidateQuestionTexts = [...candidateQuestionTexts, displayedAnswerText]
              .map((item) => cleanJobKnowledgeText(item, 260))
              .filter(Boolean)
              .slice(0, MAX_CANDIDATE_QUESTIONS);
            candidateQuestionTextsRef.current[candidateQuestionKey] = candidateQuestionTexts;
          }

          const answerTextForSave = isCandidateQuestion
            ? buildCandidateQuestionReportAnswer(candidateQuestionTexts, candidateDoneAsking)
            : displayedAnswerText;
          const rawAnswerTextForSave = isCandidateQuestion
            ? answerTextForSave
            : trimmedRawText || displayedAnswerText;

          const startBackgroundVideoUpload = pendingUpload
            ? () => {
                interviewVideoUploadQueue.enqueue({
                  ...pendingUpload,
                  interviewId,
                  traceId: turnTiming.traceId,
                });
              }
            : null;
          const answerSaveInput: RecordedAnswerSaveInput = {
            question: recordingQuestion,
            clientAttemptId: uploadClientAttemptId,
            answerText: answerTextForSave,
            rawAnswerText: rawAnswerTextForSave,
            transcriptQuality: nextTranscriptQuality,
            transcriptMode: (transcribeRes.data?.transcript_mode || '') as TranscriptMode,
            elapsedSeconds: uploadDurationSeconds,
          };
          setAnswer(answerTextForSave);
          setTranscriptQuality(nextTranscriptQuality);
          setTranscriptMode(answerSaveInput.transcriptMode);

          const applyConfirmedSubmit = async (submitPayload: any) => {
            debugInterviewSession('answer saved', { clientAttemptId: uploadClientAttemptId });
            pendingSubmitRetryRef.current = null;
            setPendingSubmitAttemptId(null);
            setSubmitError('');
            setIsRetryingSubmit(false);
            setSubmitActivity('idle');
            if (candidateTurnId) {
              updateConversationTurn(candidateTurnId, { status: 'saved' });
            }

            const responseQuestions = Array.isArray(submitPayload?.questions)
              ? submitPayload.questions as InterviewQuestion[]
              : questions;
            if (Array.isArray(submitPayload?.questions) && submitPayload.questions.length) {
              setLocalQuestions(submitPayload.questions as InterviewQuestion[]);
              setQuestions(submitPayload.questions as InterviewQuestion[]);
            }

            const nextQuestionId = Number(
              submitPayload?.next_question?.id ||
              submitPayload?.progress?.current_question_id ||
              0
            );
            const resolvedNextIdx = nextQuestionId > 0
              ? responseQuestions.findIndex((item) => Number(item.id) === nextQuestionId)
              : -1;
            const fallbackNextIdx = nextIdx < responseQuestions.length ? nextIdx : -1;
            const finalNextIdx = resolvedNextIdx >= 0 ? resolvedNextIdx : fallbackNextIdx;

            if (finalNextIdx < 0 || Boolean(submitPayload?.progress?.can_finish)) {
              const closingText = buildTemiClosingUtterance();
              const candidateQuestionResponseText = isCandidateQuestion && !candidateDoneAsking
                ? buildCandidateQuestionResponse({
                    question: recordingQuestion,
                    answerText: displayedAnswerText,
                    jobDetail: jobDetailRef.current,
                  })
                : '';
              const finishSessionAfterFinalResponse = () => {
                setCurrentIdx(responseQuestions.length);
                setPhase('closing');
                speakTemiTranscriptTurn(closingText, () => {
                  setPhase('finishing');
                }, {
                  questionId: null,
                });
              };

              if (candidateQuestionResponseText) {
                const remainingCandidateQuestions = Math.max(0, MAX_CANDIDATE_QUESTIONS - candidateQuestionTexts.length);
                setPhase(remainingCandidateQuestions > 0 ? 'responding' : 'closing');
                speakTemiTranscriptTurn(candidateQuestionResponseText, () => {
                  if (remainingCandidateQuestions > 0) {
                    const followupPrompt = buildCandidateQuestionFollowupPrompt(remainingCandidateQuestions);
                    speakTemiTranscriptTurn(followupPrompt, () => {
                      startTimeRef.current = Date.now();
                      setPhase('idle');
                    }, {
                      questionId: recordingQuestionId,
                    });
                    return;
                  }

                  finishSessionAfterFinalResponse();
                }, {
                  questionId: recordingQuestionId,
                });
                return;
              }

              finishSessionAfterFinalResponse();
            } else {
              setCurrentIdx(finalNextIdx);
              setPhase('ai_speaking');
            }
          };

          const submitCurrentTurn = async () => {
            await submitAttemptCoordinatorRef.current.execute(
              uploadClientAttemptId,
              () => saveRecordedAnswer(answerSaveInput),
              applyConfirmedSubmit
            );
          };
          pendingSubmitRetryRef.current = {
            attemptId: uploadClientAttemptId,
            retry: submitCurrentTurn,
          };
          setPendingSubmitAttemptId(uploadClientAttemptId);
          await submitAnswerWithoutWaitingForVideo({
            startVideoUpload: startBackgroundVideoUpload,
            submitAnswer: submitCurrentTurn,
          });
        } catch (err) {
          debugInterviewSession('recording processing failed', err);
          if (candidateTurnId) {
            updateConversationTurn(candidateTurnId, { status: 'error' });
          }
          const recoveryFailure = err as Partial<SubmitRecoveryFailure>;
          const recoveryDecision = resolveSubmitFailureUiDecision(recoveryFailure);
          setSubmitActivity('idle');
          if (!recoveryDecision.retainAttempt) {
            pendingSubmitRetryRef.current = null;
            setPendingSubmitAttemptId(null);
          }
          const recoveryMessage = recoveryDecision.retainAttempt
            ? 'TEMI sedang memastikan jawaban tersimpan secara otomatis.'
            : getUserFacingError(
                recoveryFailure.originalError || err,
                'Jawaban belum dapat disimpan. Silakan periksa pesan lalu coba lagi.'
              ).message;
          setSubmitError(recoveryMessage);
          setPhase(recoveryDecision.phase);
          if (!recoveryDecision.retainAttempt) {
            void technicalInterruptionHandlerRef.current(
              'ANSWER_RECONCILIATION_EXHAUSTED',
              'CANDIDATE_AUDIO_CAPTURED',
            );
          }
        } finally {
          setIsTranscribing(false);
          releaseRecordingControls();
          if (backgroundSaveRecordersRef.current.delete(recorder)) {
            finishBackgroundSave();
          }
        }
      };
      audioStopFallbackRef.current = completeStoppedRecording;
      recorder.onstop = () => {
        debugInterviewSession('audio recorder onstop fired');
        void completeStoppedRecording();
      };
      recorder.onerror = () => {
        debugInterviewSession('audio recorder error fired');
        markActiveVadTermination('recorder_error');
        clearNoSpeechPromptTimer();
        if (audioStopTimeoutRef.current !== null) {
          window.clearTimeout(audioStopTimeoutRef.current);
          audioStopTimeoutRef.current = null;
        }
        if (audioStopFallbackRef.current === completeStoppedRecording) {
          audioStopFallbackRef.current = null;
        }
        stopSilenceMonitor();
        recorderStartLockRef.current = false;
        setIsTranscribing(false);
        setIsUserSpeaking(false);
        if (backgroundSaveRecordersRef.current.delete(recorder)) {
          finishBackgroundSave();
        }
        if (activeVadMeasurementRef.current?.identity === vadMeasurement.identity) {
          activeVadMeasurementRef.current = null;
        }
        handleNoAnswerPrompt(recordingQuestionId);
      };

      recorder.onstart = () => {
        if (vadMeasurement.recorderStartedAt !== null) return;
        vadMeasurement.recorderStartedAt = interviewPerformanceNow();
        const deliveryDurationMs = measuredDurationBetween(
          vadMeasurement.deliveryCompletedAt,
          vadMeasurement.recorderStartedAt,
        );
        if (deliveryDurationMs !== null) {
          emitVadMeasurementMetricOnce(
            vadMeasurement,
            'delivery_to_recorder_start',
            'turn_delivery_to_recorder_start_ms',
            { stage: 'media_recorder_onstart', outcome: 'started' },
            deliveryDurationMs,
          );
        }
        maybeEmitRecorderStartToFirstSpeech(vadMeasurement);
      };

      recorder.start();
      videoRecorder.start();
      recRef.current = recorder;
      videoRecRef.current = videoRecorder;
      noSpeechTimeoutRef.current = window.setTimeout(() => {
        const activeRecorder = recRef.current as MediaRecorder | null;
        const monitor = silenceMonitorRef.current;
        if (activeRecorder !== recorder || recorder.state !== 'recording') return;
        const decidedAt = interviewPerformanceNow();
        if (monitor) {
          const transition = evaluateInterviewVadInitialNoSpeech(
            monitor.policyState,
            decidedAt,
          );
          monitor.policyState = transition.state;
          if (!transition.events.some((event) => (
            event.type === 'terminate' && event.reason === 'initial_no_speech'
          ))) return;
        }

        debugInterviewSession('no speech timeout reached', {
          questionId: recordingQuestionId,
          timeoutMs: INTERVIEW_VAD_POLICY.initialNoSpeechMs,
        });
        noSpeechPromptOnStopRef.current = { questionId: recordingQuestionId };
        discardRecordingOnStopRef.current = true;
        markActiveVadTermination('initial_no_speech', decidedAt);
        setSubmitError('');
        setIsTranscribing(false);
        setIsUserSpeaking(false);
        try { recorder.requestData?.(); } catch (_) {}
        try {
          markActiveRecorderStopRequest(recorder);
          recorder.stop();
        } catch (_) {
          noSpeechPromptOnStopRef.current = null;
          discardRecordingOnStopRef.current = false;
          releaseRecordingControls();
          handleNoAnswerPrompt(recordingQuestionId);
        }
      }, INTERVIEW_VAD_POLICY.initialNoSpeechMs);
      setPhase('recording');
      return true;
    };

    try {
      const orderedCandidates = [...mimeCandidates, ''];
      const orderedVideoCandidates = [...videoMimeCandidates, ''];
      for (const mimeType of orderedCandidates) {
        try {
          for (const videoMimeType of orderedVideoCandidates) {
            try {
              if (tryStartRecorder(mimeType, videoMimeType)) {
                return;
              }
            } catch (_) {}
          }
        } catch (_) {
          if (recRef.current && typeof recRef.current.stop === 'function') {
            try { recRef.current.stop(); } catch (_) {}
          }
          if (videoRecRef.current && typeof videoRecRef.current.stop === 'function') {
            try { videoRecRef.current.stop(); } catch (_) {}
          }
        }
      }

      recorderStartLockRef.current = false;
      handleMediaFailure('lost', 'Perekam audio belum tersedia. TEMI akan mencoba menyambungkan ulang otomatis.');
    } catch (_) {
      stopSilenceMonitor();
      recorderStartLockRef.current = false;
      handleMediaFailure('lost', 'Perekam audio terputus. TEMI akan mencoba menyambungkan ulang otomatis.');
    }
  }, [
    clearNoSpeechPromptTimer,
    currentIdx,
    appendConversationTurn,
    finishBackgroundSave,
    finishCurrentRecording,
    handleNoAnswerPrompt,
    handleMediaFailure,
    interviewId,
    isLikelySilentRecording,
    jobIdNum,
    mediaAvailability,
    emitVadMeasurementMetricOnce,
    markActiveRecorderStopRequest,
    markActiveVadTermination,
    markVadBlobReady,
    markVadSttStart,
    maybeEmitRecorderStartToFirstSpeech,
    removeConversationTurn,
    questions,
    saveRecordedAnswer,
    setQuestions,
    speakTemiTranscriptTurn,
    startSilenceMonitor,
    stopSilenceMonitor,
    stopTtsPlayback,
    updateConversationTurn,
  ]);

  const reconcilePendingAnswerAutomatically = useCallback(async () => {
    const pendingSubmit = pendingSubmitRetryRef.current;
    if (!pendingSubmit || submitRetryLockRef.current) return;

    submitRetryLockRef.current = true;
    setIsRetryingSubmit(true);
    setSubmitActivity('reconciling');
    setSubmitError('');
    setPhase('submitting');
    try {
      await pendingSubmit.retry();
    } catch (err) {
      const recoveryFailure = err as Partial<SubmitRecoveryFailure>;
      const recoveryDecision = resolveSubmitFailureUiDecision(recoveryFailure);
      if (!recoveryDecision.retainAttempt) {
        pendingSubmitRetryRef.current = null;
        setPendingSubmitAttemptId(null);
      }
      setSubmitError(recoveryDecision.retainAttempt
        ? 'TEMI masih memastikan jawaban tersimpan. Proses akan dilanjutkan otomatis.'
        : getUserFacingError(
            recoveryFailure.originalError || err,
            'Jawaban belum dapat disimpan. Silakan rekam jawaban baru bila diperlukan.'
          ).message);
      setSubmitActivity('idle');
      setPhase(recoveryDecision.phase);
      if (!recoveryDecision.retainAttempt) {
        void technicalInterruptionHandlerRef.current(
          'ANSWER_RECONCILIATION_EXHAUSTED',
          'CANDIDATE_AUDIO_CAPTURED',
        );
      }
    } finally {
      submitRetryLockRef.current = false;
      setIsRetryingSubmit(false);
    }
  }, []);

  useEffect(() => {
    if (phase !== 'review' || !pendingSubmitAttemptId || technicalInterruptionPromiseRef.current) {
      return undefined;
    }
    const timerId = window.setTimeout(() => {
      void reconcilePendingAnswerAutomatically();
    }, 2500);
    return () => window.clearTimeout(timerId);
  }, [pendingSubmitAttemptId, phase, reconcilePendingAnswerAutomatically]);

  // ── Finish ────────────────────────────────────────────────────
  const handleFinish = useCallback(async () => {
    if (finishRequestedRef.current || backgroundSaveCountRef.current > 0) {
      return;
    }

    finishRequestedRef.current = true;
    setFinishing(true);
    setSubmitError('');
    const totalSeconds = Math.round((Date.now() - sessionStartRef.current) / 1000);
    try {
      shutdownSessionMedia();
      await finishCompletionCoordinatorRef.current.execute(
        String(interviewId),
        () => finishInterviewWithRecovery(
          () => interviewService.finish(interviewId, totalSeconds),
          () => interviewService.getProcessing(interviewId),
          {
            maxFinishAttempts: 2,
            maxReconcilePolls: 3,
            reconcileDelayMs: 1200,
          }
        ),
        async () => {
          const currentUserId = readAuthUserId();
          if (currentUserId) {
            clearPendingAnswerRecovery({ userId: currentUserId, interviewId });
          }
          setStatus('completed');
          navigate(`/interview/result/${jobId}`, {
            state: { interviewId, outsourceId: outsourceIdVal, jobId: jobIdNum },
          });
        }
      );
    } catch (err: any) {
      setSubmitError(`${getUserFacingError(err, 'Status penyelesaian interview belum dapat dipastikan.').message} TEMI akan memastikan statusnya kembali otomatis.`);
      finishRequestedRef.current = false;
      setFinishing(false);
      if (finishRetryTimerRef.current !== null) {
        window.clearTimeout(finishRetryTimerRef.current);
      }
      finishRetryTimerRef.current = window.setTimeout(() => {
        finishRetryTimerRef.current = null;
        setFinishRetryGeneration((current) => current + 1);
      }, 3000);
    }
  }, [interviewId, jobId, jobIdNum, navigate, outsourceIdVal, setStatus, shutdownSessionMedia]);

  useEffect(() => {
    if (phase !== 'finishing') return undefined;

    shutdownSessionMedia();
    setIsUserSpeaking(false);

    const timerId = window.setTimeout(() => {
      if (backgroundSaveCountRef.current === 0) {
        void handleFinish();
      }
    }, backgroundSaveCountRef.current > 0 ? 900 : 650);

    return () => window.clearTimeout(timerId);
  }, [backgroundSaveCount, finishRetryGeneration, handleFinish, phase, shutdownSessionMedia]);

  const currentQuestion = questions[currentIdx];
  const transcriptQualityScore = Number.isFinite(Number(transcriptQuality?.score))
    ? Number(transcriptQuality?.score)
    : null;
  const transcriptQualityBand = resolveTranscriptQualityBand(transcriptQuality);
  const requiresTranscriptReview = transcriptQualityBand === 'review' || transcriptQualityBand === 'critical';
  const isLowQualityScore = transcriptQualityBand === 'critical';
  const prefersRawTranscript = Boolean(transcriptQuality?.prefer_raw);
  const riskyShiftDetected = Boolean(transcriptQuality?.risky_shift_detected);
  const missingGlossaryTerms = Array.isArray(transcriptQuality?.missing_glossary_terms)
    ? transcriptQuality.missing_glossary_terms
    : [];
  const riskyShiftTerms = Array.isArray(transcriptQuality?.risky_shift_terms)
    ? transcriptQuality.risky_shift_terms
    : [];
  const shouldShowTranscriptNotice = phase === 'review' && !isTranscribing && Boolean(answer.trim()) && Boolean(transcriptNotice);
  const shouldShowAnswerNotice = (phase === 'submitting' || phase === 'ai_speaking' || phase === 'responding' || phase === 'closing') && Boolean(answerNotice);
  const hasPendingBackgroundSaves = backgroundSaveCount > 0;
  const activeVideoUploads = videoUploadStates.filter((upload) => (
    upload.status === 'queued'
    || upload.status === 'uploading'
    || upload.status === 'retry_waiting'
  ));
  const hasReviewContent = Boolean(answer.trim()) || Boolean(transcribeError);
  const isMediaReady = mediaAvailability === 'ready' && cameraReady && !cameraError;
  const mediaRecoveryVisible = phase !== 'done' && phase !== 'finishing' && (mediaAvailability === 'blocked' || mediaAvailability === 'lost');
  const mediaRecoveryTitle = mediaAvailability === 'lost'
    ? 'Kamera atau mikrofon terputus'
    : 'Kamera atau mikrofon belum aktif';

  useEffect(() => {
    if (!AUTO_CONVERSATION_ENABLED) return undefined;
    if (phase !== 'idle') return undefined;
    if (!currentQuestion || !isMediaReady || aiSpeaking) return undefined;
    const expectedDeliveryIdentity = buildAuthoritativeQuestionDeliveryId({
      interviewId,
      questionId: Number(currentQuestion.id),
      questionType: currentQuestion.question_type,
      questionIndex: currentQuestion.question_index,
    });
    if (
      questionDelivery.state !== 'delivered'
      || questionDelivery.deliveryId === null
      || authoritativeQuestionIdentityRef.current !== expectedDeliveryIdentity
      || deliveredQuestionIdentityRef.current !== expectedDeliveryIdentity
    ) return undefined;

    const timerId = window.setTimeout(() => {
      const activeTurn = activeTurnTimingRef.current;
      if (activeTurn?.firstAudioLogged && !activeTurn.listeningLogged) {
        activeTurn.listeningLogged = true;
        trackInterviewDuration('turn_listening_resume_ms', activeTurn.startedAt, {
          traceId: activeTurn.traceId,
          interviewId,
          questionId: Number(currentQuestion.id) || activeTurn.questionId,
          clientAttemptId: activeTurn.clientAttemptId,
          outcome: 'listening',
        });
      }
      startRecording();
    }, AUTO_LISTEN_DELAY_MS);

    return () => window.clearTimeout(timerId);
  }, [aiSpeaking, currentQuestion, interviewId, isMediaReady, phase, questionDelivery, startRecording]);

  const shouldShowReviewDetails = Boolean(
    transcriptQualityScore !== null ||
    transcriptNotice ||
    missingGlossaryTerms.length > 0 ||
    riskyShiftTerms.length > 0
  );

  const reviewCardState = resolveReviewCardState({
    transcribeError,
    isLowQualityScore,
    requiresTranscriptReview,
    shouldShowTranscriptNotice,
    transcriptMode,
  });
  const requiresManualTranscriptConfirmation = reviewCardState === 'review';
  const reviewInstructionText = requiresManualTranscriptConfirmation
    ? 'Jawaban tersimpan otomatis di background.'
    : 'TEMI akan meminta jawaban kembali secara otomatis.';
  const needsReviewAction = reviewCardState === 'critical';
  const reviewDockVisible = phase === 'review' && !isTranscribing && hasReviewContent && needsReviewAction;

  const reviewCardContent = (() => {
    if (reviewCardState === 'critical') {
      return {
        title: 'Jawaban belum terbaca',
        description: transcribeError
          ? 'Suara belum terbaca dengan aman. TEMI akan meminta jawaban kembali.'
          : 'Sistem mendeteksi kualitas transkrip belum cukup aman. TEMI akan meminta jawaban kembali.',
      };
    }

    if (reviewCardState === 'review') {
      return {
        title: 'Perlu ditinjau',
        description: riskyShiftDetected || prefersRawTranscript || transcriptMode === 'raw_safe'
          ? 'Sistem mempertahankan transkrip asli untuk menjaga makna jawabanmu. Jawaban akan tetap disimpan otomatis.'
          : 'Ada beberapa istilah yang perlu dicek sistem. Jawaban akan tetap disimpan otomatis.',
      };
    }

    return {
      title: 'Jawaban tersimpan otomatis',
      description: 'TEMI menyimpan jawaban di background dan langsung menyiapkan pertanyaan berikutnya.',
    };
  })();

  const avatarIntent: AvatarIntent = submitError || cameraError || transcribeError
    ? isLowQualityScore ? 'low_quality' : 'warning'
    : phase === 'done' || phase === 'finishing'
    ? 'celebrating'
    : phase === 'submitting' || isTranscribing
    ? 'processing'
    : phase === 'review'
    ? 'reviewing'
    : phase === 'recording'
    ? isLowQualityScore ? 'evaluating' : 'encouraging'
    : phase === 'ai_speaking' || phase === 'responding' || phase === 'prompting' || phase === 'closing' || aiSpeaking
    ? 'explaining'
    : 'encouraging';

  const avatarEmotion: AvatarEmotion | undefined = submitError || cameraError || transcribeError
    ? isLowQualityScore ? 'serious' : 'surprised'
    : phase === 'done'
    ? 'happy'
    : undefined;

  const avatarState = useMemo(() => ({
    phase,
    aiIntent: avatarIntent,
    emotion: avatarEmotion,
    speaking: aiSpeaking || phase === 'ai_speaking' || phase === 'responding' || phase === 'prompting' || phase === 'closing',
    listening: phase === 'recording' && !isTranscribing,
    thinking: phase === 'submitting' || phase === 'review' || isTranscribing,
    error: Boolean(submitError || cameraError || transcribeError),
    warning: Boolean(isLowQualityScore || requiresTranscriptReview),
    celebrating: phase === 'done',
  }), [
    aiSpeaking,
    avatarEmotion,
    avatarIntent,
    cameraError,
    isLowQualityScore,
    isTranscribing,
    phase,
    requiresTranscriptReview,
    submitError,
    transcribeError,
  ]);

  const avatarStageTone = avatarState.error
    ? 'warning'
    : avatarState.celebrating
    ? 'success'
    : avatarState.thinking
    ? 'thinking'
    : avatarState.speaking
    ? 'speaking'
    : avatarState.listening
    ? 'listening'
    : 'idle';
  const sttRecoveryStatusText = sttRecoveryPhase === 'exhausted'
    ? 'Koneksi belum pulih. Jawabanmu belum dapat diproses.'
    : sttRecoveryPhase === 'connectivity_wait'
    ? 'Koneksi sedang bermasalah. Jawabanmu tetap tersimpan dan TEMI akan mencoba lagi otomatis.'
    : sttRecoveryPhase === 'fast_retry_wait'
    ? 'TEMI sedang mencoba membaca jawabanmu kembali…'
    : 'Mengubah jawaban menjadi teks…';
  const liveConversationStatus = phase === 'recording'
    ? isUserSpeaking
      ? 'Jawaban sedang terdeteksi'
      : isTranscribing
      ? sttRecoveryStatusText
      : 'TEMI mendengarkan jawaban'
    : isTranscribing
    ? sttRecoveryStatusText
    : submitActivity === 'reconciling'
    ? 'Memastikan jawaban sudah tersimpan…'
    : phase === 'submitting' || submitActivity === 'evaluating'
    ? 'TEMI sedang memahami jawaban…'
    : phase === 'finishing'
    ? hasPendingBackgroundSaves
      ? 'Menyimpan jawaban terakhir'
      : 'Mengalihkan ke halaman hasil'
    : phase === 'responding'
    ? 'TEMI merespons kandidat'
    : phase === 'prompting'
    ? 'TEMI memastikan kamu masih siap menjawab'
    : phase === 'delivery_recovery'
    ? 'Koneksi sedang dipulihkan sebelum pertanyaan dilanjutkan'
    : phase === 'closing'
    ? 'TEMI menutup sesi interview'
    : aiSpeaking || phase === 'greeting' || phase === 'ai_speaking'
    ? 'TEMI membacakan pertanyaan'
    : phase === 'done'
    ? 'Interview selesai'
    : 'Menunggu giliran bicara';
  if (phase === 'technical_interrupted') {
    return (
      <div className="iv-fullscreen iv-fullscreen--loading" aria-live="polite">
        <FiAlertTriangle aria-hidden="true" />
        <p>{technicalInterruptionMessage || 'Interview dihentikan karena kendala teknis. Kamu akan dialihkan otomatis.'}</p>
      </div>
    );
  }

  if (phase === 'loading' || (!currentQuestion && phase !== 'done' && phase !== 'greeting' && phase !== 'responding' && phase !== 'closing' && phase !== 'finishing')) {
    return (
      <div className="iv-fullscreen iv-fullscreen--loading">
        <Spinner animation="border" variant="light" />
        <p>Mempersiapkan sesi interview...</p>
      </div>
    );
  }

  return (
    <div className={`iv-fullscreen${phase === 'done' ? ' iv-fullscreen--done' : ''}${phase === 'finishing' ? ' iv-fullscreen--ending' : ''}${reviewDockVisible ? ' iv-fullscreen--review' : ''}${mediaRecoveryVisible ? ' iv-fullscreen--media-blocked' : ''}${!transcriptPanelOpen ? ' iv-fullscreen--transcript-closed' : ''}`}>
      {/* ── Camera background ── */}
      <div className={`iv-fullscreen__temi-stage iv-fullscreen__temi-stage--${avatarStageTone}`}>
        <div className="iv-fullscreen__temi-grid" />
        <AvatarController avatarState={avatarState} size="large" />
        <div className="iv-fullscreen__temi-card-label">
          <span>TEMI</span>
          <span
            className={`iv-soundwave ${
              aiSpeaking || phase === 'ai_speaking' || phase === 'responding' || phase === 'prompting' || phase === 'closing'
                ? 'iv-soundwave--active'
                : 'iv-soundwave--idle'
            }`}
            aria-hidden="true"
          >
            <i /><i /><i /><i />
          </span>
        </div>
      </div>

      <div className={`iv-fullscreen__candidate-pip${phase === 'recording' && !isTranscribing ? ' iv-fullscreen__candidate-pip--recording' : ''}${isUserSpeaking ? ' iv-fullscreen__candidate-pip--speaking' : ''}${cameraError ? ' iv-fullscreen__candidate-pip--error' : ''}`}>
        <video ref={videoRef} className="iv-fullscreen__self-video" autoPlay muted playsInline />
        <div className="iv-fullscreen__candidate-label">
          <span>{candidateDisplayName}</span>
          <span
            className={`iv-soundwave iv-soundwave--candidate ${isUserSpeaking ? 'iv-soundwave--active' : 'iv-soundwave--idle'}`}
            aria-hidden="true"
          >
            <i /><i /><i /><i />
          </span>
        </div>
      </div>
      <div className="iv-fullscreen__job-caption" title={sessionJobTitle}>
        <span>{sessionJobTitle}</span>
      </div>
      {mediaAvailability === 'loading' && !cameraReady && !cameraError && (
        <div className="iv-fullscreen__cam-status">
          <Spinner animation="border" variant="light" size="sm" /> Mempersiapkan kamera...
        </div>
      )}
      {mediaRecoveryVisible && (
        <div className="iv-fullscreen__media-recovery">
          <div className="iv-fullscreen__media-card">
            <div className="iv-fullscreen__media-badge">Perlu izin perangkat</div>
            <h2 className="iv-fullscreen__media-title">{mediaRecoveryTitle}</h2>
            <p className="iv-fullscreen__media-copy">
              Interview ini membutuhkan kamera dan mikrofon aktif untuk merekam jawaban Anda. TEMI sedang mencoba menyambungkan perangkat secara otomatis.
            </p>
            <div className="iv-fullscreen__media-steps">
              <div className="iv-fullscreen__media-step">
                <span className="iv-fullscreen__media-step-index">1</span>
                <span>Klik ikon gembok atau kamera di address bar browser.</span>
              </div>
              <div className="iv-fullscreen__media-step">
                <span className="iv-fullscreen__media-step-index">2</span>
                <span>Ubah izin kamera dan mikrofon menjadi <strong>Allow</strong>.</span>
              </div>
              <div className="iv-fullscreen__media-step">
                <span className="iv-fullscreen__media-step-index">3</span>
                <span>Kembali ke halaman ini. TEMI akan mendeteksi izin perangkat secara otomatis.</span>
              </div>
            </div>
            {cameraError && <p className="iv-fullscreen__media-error">{cameraError}</p>}
          </div>
        </div>
      )}

      {phase === 'delivery_recovery' && (
        <div className="iv-fullscreen__media-recovery" aria-live="polite">
          <div className="iv-fullscreen__media-card">
            <div className="iv-fullscreen__media-badge">Memulihkan koneksi</div>
            <h2 className="iv-fullscreen__media-title">TEMI sedang menyiapkan ulang pertanyaan</h2>
            <p className="iv-fullscreen__media-copy">
              Koneksi sedang bermasalah. TEMI akan mengulang pertanyaan lengkap setelah koneksi kembali stabil.
            </p>
          </div>
        </div>
      )}

      {transcriptPanelOpen && (
        <aside className="iv-fullscreen__conversation-panel" aria-label="Live transkip percakapan interview">
          <div className="iv-fullscreen__conversation-head">
            <div>
              <strong>Live Transkip</strong>
            </div>
            <button
              type="button"
              className="iv-fullscreen__conversation-close"
              onClick={() => setTranscriptPanelOpen(false)}
              aria-label="Tutup live transkip"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>

          <div className="iv-fullscreen__conversation-body">
            {conversationTurns.length === 0 && (
              <div className="iv-fullscreen__conversation-empty">
                Transkip percakapan akan muncul di sini.
              </div>
            )}

            {conversationTurns.map((turn) => (
              <div
                key={turn.id}
                className={`iv-fullscreen__conversation-turn iv-fullscreen__conversation-turn--${turn.speaker}${turn.status ? ` iv-fullscreen__conversation-turn--${turn.status}` : ''}`}
              >
                <div className="iv-fullscreen__conversation-speaker">
                  <span>{turn.speaker === 'temi' ? 'TEMI' : turn.speaker === 'candidate' ? candidateDisplayName : 'Sistem'}</span>
                  {turn.status === 'speaking' && <small>live</small>}
                  {turn.status === 'processing' && <small>diproses</small>}
                  {turn.status === 'saved' && <small>tersimpan</small>}
                  {turn.status === 'error' && <small>gagal</small>}
                </div>
                <p>{(turn.status === 'speaking' || turn.status === 'processing') && !turn.text ? <TypingDots /> : turn.text}</p>
              </div>
            ))}

            {(phase === 'recording' || isTranscribing || phase === 'submitting') && (
              <div className="iv-fullscreen__conversation-turn iv-fullscreen__conversation-turn--system iv-fullscreen__conversation-turn--live">
                <div className="iv-fullscreen__conversation-speaker">
                  <span>Sistem</span>
                  <small>live</small>
                </div>
                <p>{liveConversationStatus}</p>
              </div>
            )}

            <div ref={conversationEndRef} />
          </div>
        </aside>
      )}

      {phase !== 'finishing' && (
        <div className="iv-fullscreen__quick-actions" aria-label="Aksi cepat interview">
          <button
            type="button"
            className="iv-fullscreen__quick-btn"
            onClick={() => setInfoModalOpen(true)}
            aria-label="Lihat tata cara interview"
          >
            <FiInfo aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`iv-fullscreen__quick-btn${transcriptPanelOpen ? ' iv-fullscreen__quick-btn--active' : ''}`}
            onClick={() => setTranscriptPanelOpen((isOpen) => !isOpen)}
            aria-label={transcriptPanelOpen ? 'Tutup live transkip' : 'Buka live transkip'}
          >
            <FiMessageCircle aria-hidden="true" />
          </button>
        </div>
      )}

      {infoModalOpen && (
        <div className="iv-fullscreen__info-modal" role="dialog" aria-modal="true" aria-labelledby="iv-info-title">
          <div className="iv-fullscreen__info-card">
            <div className="iv-fullscreen__info-head">
              <div>
                <span>Tata Cara</span>
                <h2 id="iv-info-title">Interview dengan TEMI</h2>
              </div>
              <button
                type="button"
                className="iv-fullscreen__info-close"
                onClick={() => setInfoModalOpen(false)}
                aria-label="Tutup tata cara interview"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="iv-fullscreen__info-body">
              <p>Pastikan wajah terlihat jelas dan suara tidak terlalu jauh dari mikrofon.</p>
              <p>Dengarkan pertanyaan TEMI sampai selesai, lalu jawab dengan contoh pengalaman yang spesifik.</p>
              <p>Sistem akan merekam jawaban otomatis dan lanjut setelah suara berhenti terdeteksi.</p>
              <p>Jika izin kamera atau mikrofon terputus, aktifkan kembali dari browser lalu lanjutkan sesi.</p>
            </div>
          </div>
        </div>
      )}

      {exitConfirmOpen && (
        <div className="iv-fullscreen__exit-modal" role="dialog" aria-modal="true" aria-labelledby="iv-exit-title">
          <div className="iv-fullscreen__exit-card">
            <div className="iv-fullscreen__exit-icon" aria-hidden="true">
              <FiAlertTriangle />
            </div>
            <div className="iv-fullscreen__exit-content">
              <span className="iv-fullscreen__exit-eyebrow">Konfirmasi keluar</span>
              <h2 id="iv-exit-title">Keluar dari sesi interview?</h2>
              <p>
                Interview masih berlangsung. Jika keluar sekarang, kamera dan mikrofon akan dimatikan,
                dan jawaban yang belum selesai diproses bisa tidak tersimpan.
              </p>
              <div className="iv-fullscreen__exit-note">
                Saran: tetap di halaman ini sampai TEMI menyelesaikan sesi dan halaman hasil terbuka.
              </div>
            </div>
            <div className="iv-fullscreen__exit-actions">
              <button
                type="button"
                className="iv-fullscreen__exit-btn iv-fullscreen__exit-btn--stay"
                onClick={cancelSessionExit}
              >
                Tetap di interview
              </button>
              <button
                type="button"
                className="iv-fullscreen__exit-btn iv-fullscreen__exit-btn--leave"
                onClick={confirmSessionExit}
              >
                Keluar dari interview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Ending transition ── */}
      {phase === 'finishing' && (
        <div className="iv-fullscreen__ending-overlay" aria-live="polite">
          <div className="iv-fullscreen__ending-status">
            <span className="iv-fullscreen__ending-dot" />
            <strong>Interview selesai</strong>
            <span>
              {hasPendingBackgroundSaves
                ? 'Menyimpan jawaban terakhir...'
                : finishing
                ? 'Menyiapkan halaman hasil...'
                : 'Mengakhiri sesi...'}
            </span>
            {submitError && <small>{submitError}</small>}
          </div>
        </div>
      )}

      {/* ── Bottom controls ── */}
      {phase !== 'done' && phase !== 'finishing' && (
        <div className="iv-fullscreen__bottom-bar">

          {/* Live recording transcript */}
          {phase === 'recording' && answer && (
            <div className="iv-fullscreen__transcript iv-fullscreen__transcript--live">
              "{answer}"
            </div>
          )}

          {/* Transcribing spinner */}
          {isTranscribing && phase !== 'submitting' && (
            <div className="iv-fullscreen__transcribing">
              {sttRecoveryPhase !== 'exhausted' && <Spinner animation="border" size="sm" variant="light" />}
              <span>{sttRecoveryStatusText}</span>
            </div>
          )}

          {/* Transcript result */}
          {reviewDockVisible && (
            <div className="iv-fullscreen__transcript-wrap">
              <div className={`iv-fullscreen__review-card iv-fullscreen__review-card--${reviewCardState}`}>
                <div className="iv-fullscreen__review-card-head">
                  <div>
                    <strong>{reviewCardContent.title}</strong>
                    <span>{reviewCardContent.description}</span>
                  </div>
                </div>

                {shouldShowReviewDetails && (
                  <details className="iv-fullscreen__review-details">
                    <summary>Lihat detail sistem</summary>
                    <div className="iv-fullscreen__review-details-body">
                      {transcriptNotice && <p>{transcriptNotice}</p>}
                      {transcriptQualityScore !== null && (
                        <p>Skor kualitas transkrip: {transcriptQualityScore} / 100</p>
                      )}
                      {riskyShiftTerms.length > 0 && (
                        <p>Istilah yang perlu dicek: {riskyShiftTerms.join(', ')}</p>
                      )}
                      {!riskyShiftTerms.length && missingGlossaryTerms.length > 0 && (
                        <p>Istilah penting yang berubah: {missingGlossaryTerms.join(', ')}</p>
                      )}
                    </div>
                  </details>
                )}
              </div>
              {needsReviewAction && (
                <p className="iv-fullscreen__review-helper">{reviewInstructionText}</p>
              )}
            </div>
          )}

          {activeVideoUploads.length > 0 && (
            <div className="iv-fullscreen__video-upload-status" aria-live="polite">
              <span>
                {activeVideoUploads.length} video jawaban sedang diamankan di background. Interview tetap berjalan.
              </span>
            </div>
          )}

          {/* Error */}
          {submitError && <p className="iv-fullscreen__error">{submitError}</p>}

          {shouldShowAnswerNotice && <p className="iv-fullscreen__hint">{answerNotice}</p>}

          {/* Controls row */}
          <div className="iv-fullscreen__controls">
            {/* Submitting spinner */}
            {phase === 'submitting' && (
              <div className="iv-mic-big iv-mic-big--submitting">
                <Spinner animation="border" variant="light" />
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewSession;
